import React, { useState, useMemo, useEffect, useCallback, useRef, useTransition } from 'react';
import { useLocation } from 'react-router-dom';
import Highcharts from 'highcharts/highstock';
import ResponsiveHighchartsReact from '../../common/ResponsiveHighchartsReact';
import GenerationProfileMethodologyAccordion from './GenerationProfileMethodologyAccordion';
import ProcessingLogHost from './ProcessingLogHost';
import SimulatorUtilizationMapsGrid from './SimulatorUtilizationMapsGrid';
import GenerationProfileRightPanel, { RightPanelMetricsCards } from './GenerationProfileRightPanel';
import GenerationProfileInputsPanel from './GenerationProfileInputsPanel';
import MobileSimulatorProfilePanels from './MobileSimulatorProfilePanels';
import DesktopInputsSidebar from './DesktopInputsSidebar';
import ApiTimingPanel from '../../ApiTimingPanel';
import {
  createEmptyInputsLive,
  syncInputsLiveRef,
  getChangedCapacityKeys,
  getChangedDispatchableKeys,
  getChangedVoSubtechTechIds,
  canRunUnifiedGenerate,
  getCapacityInputErrors,
  seedVoSubtechDraftFromCapacityByFuel,
  sumVoSubtechDraftForFuel,
  parseVoSubtechDraftToApplied,
  hasExpandableVoSubfuels,
  DEFAULT_STORAGE_DURATION_H,
  DEFAULT_PUMPED_STORAGE_DURATION_H,
} from './generationProfileInputsUtils';
import ChartFullscreenModal from '../../common/ChartFullscreenModal';
import { computeFullscreenStockChartHeight } from '../../../utils/chartFullscreenLayout';

import { 
  RefreshCw,
  ArrowUpRight,
} from 'lucide-react';
import { capacityAPI, generationProfileAPI, renewablePotentialAPI, vedaonlineAPI } from '../../../services/api';
import api from '../../../services/api';
import toast from 'react-hot-toast';
import { formatCapacityGwInput } from '../../../utils/numberFormatting';
import { getFuelColor } from '../../../utils/fuelColors';
import { CollapsiblePanelIcon, InfoIcon } from '../../icons';
import { debounce } from '../../../utils/commonUtils';
import {
  attachLegendRightClickFocus,
  isolateChartSeriesOnLegendFocus,
  HIGHCHARTS_NARROW_LAYOUT_RESPONSIVE
} from '../../../utils/highchartsConfig';
import {
  parseSimulatorScenarioFromSearch,
  normalizeSimulatorScenario,
  buildMergedScenarioInput,
  buildSimulatorScenarioV1,
  buildUrlSpecifiedKeysFromNative,
  buildUrlSpecifiedKeysFromRaw,
  encodeSimulatorScenarioSearchParam,
  SIMULATOR_SCENARIO_QUERY_KEY,
  SIMULATOR_SUPPLY_WEATHER_YEARS,
  pickInitialDemandWeatherYearFromScenario,
  pickInitialSupplyWeatherYearFromScenario,
  scenarioUrlHasExplicitDemandTwh
} from './simulatorScenarioShare';
import {
  applyRawFossilZeroDefaults,
  isRawScenarioFormat,
  mapRawCapacityToSimulatorGws,
  rawMappedToNativePayload
} from './simulatorRawTechMapping';
import { ensureSimulatorTechMappingLoaded, prefetchSimulatorTechMapping } from './simulatorTechMappingCache';
import { prefetchVoFlexTechCatalog } from './voFlexTechCatalogCache';
import SimulatorUnmappedTechBanner from './SimulatorUnmappedTechBanner';
import VedaEngineDataGapsBanner from './VedaEngineDataGapsBanner';
import { collectVreProfileDegradedReasons, formatDegradedMessage } from './vedaDegradedMessages';
import VedaEngineTracePanel from './VedaEngineTracePanel';
import {
  parseVedaLookupFromSearch,
  buildVedaRawScenarioFromCapacityMix,
  buildVedaLookupQueryString,
  shouldShowVedaDebugPanel,
  vedaLookupHasExplicitDemandTwh,
  vedaLookupExpectsReportDemand,
} from './simulatorVedaLookup';
import {
  groupCapacityByFlexFuel,
  groupSimGenByFlexFuel,
  enrichFlexDispatchStack,
  findUnmappedFlexCapacity,
  classifyFlexFuel,
  applyVoSubtechCapacityOverrides,
  filterUnmappedTechsCoveredByFlexCatalog,
} from './voFuelTechBreakdown';
import HourlyChartMonthNavigator from './subComponents/HourlyChartMonthNavigator';
import HourlyChartDesktopLegend from './subComponents/HourlyChartDesktopLegend';
import HourlyGenerationAndDemandChartSkeleton from './subComponents/HourlyGenerationAndDemandChartSkeleton';
import TooltipSwitch from './subComponents/TooltipSwitch';
import { voFlexSubfuelColor, VO_FLEX_CHART_FUELS } from './utils/hourlyChartColors';
import { runHourlyDispatchSimulation } from './utils/runHourlyDispatchSimulation';
import { buildHourlyChartSeries } from './utils/buildHourlyChartSeries';
import { useSimulatorMaps } from './hooks/useSimulatorMaps';
import { useLatestRef } from './hooks/useLatestRef';
import { useStableDebouncedCallback } from './hooks/useStableDebouncedCallback';
import {
  getMonthHourBounds,
  getUtcMonthIndexForYear,
} from './subComponents/hourlyChartMonthWindow';

// Import Highcharts modules for better functionality
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsAccessibility from 'highcharts/modules/accessibility';
import HighchartsData from 'highcharts/modules/data';

// Initialize Highcharts modules
if (typeof Highcharts === 'object') {
  HighchartsMore(Highcharts);
  HighchartsExporting(Highcharts);
  HighchartsAccessibility(Highcharts);
  HighchartsData(Highcharts);
}

/** Below Tailwind `lg` (1024px): hourly chart uses compact chrome (bottom legend, no tooltip toggle). */
const HOURLY_CHART_COMPACT_MQ = '(max-width: 1023px)';

/** Below Tailwind `md` (768px): hide range-selector date inputs (mobile only; tablet keeps them). */
const HOURLY_CHART_MOBILE_MQ = '(max-width: 767px)';

/** Panel header row on compact viewports — enough vertical room so icon/text are not clipped */
const MOBILE_PANEL_HEADER_ROW_CLASS =
  'flex items-center justify-between gap-2 mb-3 min-h-10 py-1';
const MOBILE_PANEL_HEADER_TITLE_CLASS = 'card-header-figma leading-normal';

/** Panel collapse control on compact viewports */
const MOBILE_PANEL_COLLAPSE_BTN_CLASS = 'btn-panel-toggle-sm';

/** Desktop hourly chart: plot area is always 420px; legend lives in HTML above the chart. */
const HOURLY_DESKTOP_PLOT_HEIGHT = 420;
const HOURLY_DESKTOP_LEGEND_ROW_PX = 28;
const HOURLY_DESKTOP_LEGEND_ITEMS_PER_ROW = 5;
const HOURLY_DESKTOP_CHART_MARGIN_TOP = 50;
const HOURLY_DESKTOP_TOOLTIP_TOGGLE_ID = 'hourly-desktop-tooltip-toggle';

/** Mobile/tablet (< lg): shorter Highstock container — legend is HTML below the plot. */
const HOURLY_COMPACT_CHART_HEIGHT_MOBILE = 360;
const HOURLY_COMPACT_CHART_HEIGHT_TABLET = 400;
const HOURLY_COMPACT_CHART_HOST_MIN_HEIGHT_MOBILE = 280;
const HOURLY_COMPACT_CHART_HOST_MIN_HEIGHT_TABLET = 320;
const HOURLY_COMPACT_CARD_MIN_HEIGHT_MOBILE = 420;
const HOURLY_COMPACT_CARD_MIN_HEIGHT_TABLET = 460;
const HOURLY_COMPACT_NAVIGATOR_HEIGHT = 28;
const HOURLY_COMPACT_NAVIGATOR_MARGIN = 4;
const HOURLY_COMPACT_SCROLLBAR_HEIGHT = 10;

function getHourlyCompactChartHeight(isMobile) {
  return isMobile ? HOURLY_COMPACT_CHART_HEIGHT_MOBILE : HOURLY_COMPACT_CHART_HEIGHT_TABLET;
}

function getHourlyCompactChartHostMinHeight(isMobile) {
  return isMobile ? HOURLY_COMPACT_CHART_HOST_MIN_HEIGHT_MOBILE : HOURLY_COMPACT_CHART_HOST_MIN_HEIGHT_TABLET;
}

function getHourlyCompactCardMinHeight(isMobile) {
  return isMobile ? HOURLY_COMPACT_CARD_MIN_HEIGHT_MOBILE : HOURLY_COMPACT_CARD_MIN_HEIGHT_TABLET;
}

/** Fit marginBottom to navigator + scrollbar so no white band above the minimap. */
function syncCompactChartBottomMargin(chart) {
  if (!chart || chart.destroyed) return;
  const navHeight = Number(chart.navigator?.height) || HOURLY_COMPACT_NAVIGATOR_HEIGHT;
  const navMargin =
    typeof chart.options?.navigator?.margin === 'number'
      ? chart.options.navigator.margin
      : HOURLY_COMPACT_NAVIGATOR_MARGIN;
  const scrollbarOn = chart.options?.scrollbar?.enabled !== false;
  const scrollbarPad = scrollbarOn ? HOURLY_COMPACT_SCROLLBAR_HEIGHT : 0;
  const desiredMarginBottom = Math.ceil(navHeight + navMargin + scrollbarPad + 2);
  if (chart.options?.chart?.marginBottom !== desiredMarginBottom) {
    chart.update({ chart: { marginBottom: desiredMarginBottom } }, false);
  }
}

function getHourlyDesktopLegendSlotHeight(seriesCount) {
  const rows = Math.max(1, Math.ceil(seriesCount / HOURLY_DESKTOP_LEGEND_ITEMS_PER_ROW));
  return rows * HOURLY_DESKTOP_LEGEND_ROW_PX + 2;
}

/** Ensure at least one browser paint before heavy work starts. */
function waitForNextPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

const GenerationProfileChart = React.forwardRef(({ countryIso }, ref) => {
  const location = useLocation();
  const parsedShareScenario = useMemo(
    () => parseSimulatorScenarioFromSearch(location.search),
    [location.search]
  );

  const parsedVedaLookup = useMemo(
    () => (parsedShareScenario ? null : parseVedaLookupFromSearch(location.search, { defaultRegion: countryIso })),
    [location.search, parsedShareScenario, countryIso]
  );

  const showVedaDebugPanel = useMemo(
    () => shouldShowVedaDebugPanel(location.search, { defaultRegion: countryIso }),
    [location.search, countryIso]
  );

  /** When true, chartData must not overwrite demand TWh (explicit demand in ?scenario=). */
  const skipDemandAutofillFromChartDataRef = useRef(false);
  const vedaReportProfileFetchedRef = useRef(false);
  const parsedShareScenarioRef = useRef(parsedShareScenario);
  const parsedVedaLookupRef = useRef(parsedVedaLookup);
  const demandProfileSubmitGenRef = useRef(0);
  const vedaOverlayScenarioRef = useRef(null);
  const [vedaOverlayReady, setVedaOverlayReady] = useState(true);
  const [vedaFetchLoading, setVedaFetchLoading] = useState(false);
  const [vedaFetchError, setVedaFetchError] = useState(null);
  const [vedaEngineResult, setVedaEngineResult] = useState(null);
  const vedaEngineResultRef = useRef(null);
  const [voFlexCatalog, setVoFlexCatalog] = useState(null);
  const [vedaEngineTrace, setVedaEngineTrace] = useState(null);
  const [vreProfileTraces, setVreProfileTraces] = useState(null);
  const [vedaTracePanelDismissed, setVedaTracePanelDismissed] = useState(false);
  const [unmappedTechs, setUnmappedTechs] = useState([]);
  const [unmappedBannerDismissed, setUnmappedBannerDismissed] = useState(false);
  const [vedaGapsBannerDismissed, setVedaGapsBannerDismissed] = useState(false);
  /** null = no ?scenario=; Set = keys from URL; empty Set = URL load but all values system-filled */
  const [scenarioUrlSpecifiedKeys, setScenarioUrlSpecifiedKeys] = useState(null);

  /** System-filled values when loading ?scenario= (not from URL). */
  const scenarioSourceFilledClass = '!bg-amber-50';

  const urlSourceInputClass = useCallback(
    (scenarioKey, extraClass = '') => {
      const base = (extraClass || '').trim();
      if (scenarioUrlSpecifiedKeys === null) return base;
      if (scenarioUrlSpecifiedKeys.has(scenarioKey)) return base;
      return [base, scenarioSourceFilledClass].filter(Boolean).join(' ');
    },
    [scenarioUrlSpecifiedKeys, scenarioSourceFilledClass]
  );

  const [lastAppliedDemandTwhInput, setLastAppliedDemandTwhInput] = useState('');
  const demandTwhRef = React.useRef('');
  const inputsLiveRef = useRef(createEmptyInputsLive());
  const inputsPanelRef = useRef(null);

  const applyInputsPanelSeed = useCallback((patch) => {
    const next = { ...inputsLiveRef.current, ...patch };
    syncInputsLiveRef(inputsLiveRef, next);
    if (Object.prototype.hasOwnProperty.call(patch, 'demandTwhInput')) {
      demandTwhRef.current = patch.demandTwhInput ?? '';
    }
    inputsPanelRef.current?.applySeed(patch);
  }, []);

  const resetInputsPanel = useCallback(() => {
    const empty = createEmptyInputsLive();
    syncInputsLiveRef(inputsLiveRef, empty);
    demandTwhRef.current = '';
    inputsPanelRef.current?.resetAll();
  }, []);

  useEffect(() => {
    prefetchSimulatorTechMapping();
    prefetchVoFlexTechCatalog().then((catalog) => {
      if (catalog) setVoFlexCatalog(catalog);
    });
  }, []);

  useEffect(() => {
    parsedShareScenarioRef.current = parsedShareScenario;
    parsedVedaLookupRef.current = parsedVedaLookup;

    const shareExplicitDemand = scenarioUrlHasExplicitDemandTwh(parsedShareScenario);
    const vedaExplicitDemand = vedaLookupHasExplicitDemandTwh(parsedVedaLookup);
    const vedaReportDemand = vedaLookupExpectsReportDemand(parsedVedaLookup);

    skipDemandAutofillFromChartDataRef.current = shareExplicitDemand
      || vedaExplicitDemand
      || vedaReportDemand;

    setUnmappedBannerDismissed(false);
    if (!parsedShareScenario) {
      setScenarioUrlSpecifiedKeys(null);
      setUnmappedTechs([]);
    } else if (isRawScenarioFormat(parsedShareScenario)) {
      setScenarioUrlSpecifiedKeys(null);
      setUnmappedTechs([]);
    } else {
      setScenarioUrlSpecifiedKeys(buildUrlSpecifiedKeysFromNative(parsedShareScenario));
      setUnmappedTechs([]);
    }

    if (shareExplicitDemand) {
      const d = parsedShareScenario.demandTwh;
      const num = typeof d === 'number' ? d : parseFloat(d);
      if (Number.isFinite(num) && num > 0) {
        const s = String(num);
        demandTwhRef.current = s;
        applyInputsPanelSeed({ demandTwhInput: s, demandTwhError: '' });
        setLastAppliedDemandTwhInput(s);
      }
      return;
    }

    if (vedaExplicitDemand) {
      const s = String(parsedVedaLookup.demandTwh);
      demandTwhRef.current = s;
      applyInputsPanelSeed({ demandTwhInput: s, demandTwhError: '' });
      setLastAppliedDemandTwhInput(s);
    }
  }, [parsedShareScenario, parsedVedaLookup, applyInputsPanelSeed]);

  const [formData, setFormData] = useState({
    year: 2022
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState(null);
  
  const prevCountryRef = React.useRef(null); // Track previous country to detect real changes
  const prevYearRef = React.useRef(null); // Track previous year so we only full-reset on country/year change
  const prevCapacityDepsRef = React.useRef({ countryIso: null, year: null, supplyWeatherYear: null }); // For overwriteInputs when only supply weather year changes
  const prevSimulationLogSigRef = React.useRef(null);
  const [isDemandGoLoading, setIsDemandGoLoading] = useState(false);
  const chartRef = useRef(null);
  const chartContainerRef = useRef(null);
  const chartResizeWrapperRef = useRef(null);
  const [hourlyDesktopLegendHeight, setHourlyDesktopLegendHeight] = useState(
    () => getHourlyDesktopLegendSlotHeight(0)
  );
  const [hourlyChartLayoutReady, setHourlyChartLayoutReady] = useState(false);
  const [hourlyMobileFullscreenOpen, setHourlyMobileFullscreenOpen] = useState(false);
  /** Compact (mobile/tablet): visible UTC month 0–11; full year stays in memory. */
  const [hourlyVisibleMonthIndex, setHourlyVisibleMonthIndex] = useState(0);
  const pendingHourlyChartFocusMsRef = useRef(null);
  /** Skip one compact full-month fit after max-shortage (keeps the 7-day zoom). */
  const skipNextCompactMonthFitRef = useRef(false);
  const [hourlyFullscreenPlotHeight, setHourlyFullscreenPlotHeight] = useState(280);
  const [hourlyFullscreenLegendHeight, setHourlyFullscreenLegendHeight] = useState(56);
  const hourlyFullscreenBodyHeightRef = useRef(0);
  const hourlyFullscreenChartHostRef = useRef(null);
  const hourlyChartInitTokenRef = useRef(0);
  const onResizeCompleteRef = useRef(null);
  const startChartResizeRef = useRef(null);
  const scheduleDeferredChartReflowRef = useRef(null);
  const panelResizeSafetyRef = useRef(null);
  const hourlyRevealTimeoutRef = useRef(null);
  const scrollAfterFocusTimeoutRef = useRef(null);
  // Preserve user chart interactions (zoom + legend toggles) across
  // same-country input refreshes.
  const hourlyChartStateRef = useRef({
    zoom: null,
    legendVisibility: {}
  });
  /**
   * VO flex fuel collapse prefs (default collapsed = drill-down). Survives zoom,
   * Generate, and capacity edits — only cleared on country/year reset like legendVisibility.
   * Missing key or true = parent band; false = expanded sub-techs.
   */
  const [voFlexFuelCollapsed, setVoFlexFuelCollapsed] = useState({});
  const [voFlexFuelToggleBusy, setVoFlexFuelToggleBusy] = useState(false);
  const [voFlexFuelToggleActiveFuel, setVoFlexFuelToggleActiveFuel] = useState(null);
  const [isFlexTogglePending, startFlexToggleTransition] = useTransition();
  const voFlexFuelToggleGenRef = useRef(0);
  const voFlexFuelTogglePendingUnlockRef = useRef(false);
  const isRestoringHourlyChartStateRef = useRef(false);
  const [capacityLoading, setCapacityLoading] = useState(false);
  const [capacityError, setCapacityError] = useState(null);
  const [solarProfile, setSolarProfile] = useState(null);
  const [solarLoading, setSolarLoading] = useState(false);
  const [solarCapacityGW, setSolarCapacityGW] = useState(null);
  const [solarSelectedCells, setSolarSelectedCells] = useState(null);
  const [windProfile, setWindProfile] = useState(null);
  const [windLoading, setWindLoading] = useState(false);
  const [windCapacityGW, setWindCapacityGW] = useState(null);
  const [solarZonesGeoJSON, setSolarZonesGeoJSON] = useState(null);
  const [windZonesGeoJSON, setWindZonesGeoJSON] = useState(null);
  const [windOffZonesGeoJSON, setWindOffZonesGeoJSON] = useState(null);
  const [windSelectedCells, setWindSelectedCells] = useState(null);
  // Wind Offshore state
  const [windOffProfile, setWindOffProfile] = useState(null);
  const [windOffLoading, setWindOffLoading] = useState(false);
  const [windOffCapacityGW, setWindOffCapacityGW] = useState(null);
  const [windOffSelectedCells, setWindOffSelectedCells] = useState(null);
  const {
    simulatorWindMapRef,
    simulatorWindLayerRef,
    simulatorWindOffLayerRef,
    windOnshoreLayerVisibleRef,
    windOffshoreLayerVisibleRef,
    windMapLayerGeneration,
  } = useSimulatorMaps({
    solarZonesGeoJSON,
    solarSelectedCells,
    windZonesGeoJSON,
    windSelectedCells,
    windOffZonesGeoJSON,
    windOffSelectedCells,
  });

  // Selected renewable type for combined chart card
  const [hydroProfile, setHydroProfile] = useState(null);
  const [hydroCapacityGW, setHydroCapacityGW] = useState(null);
  // Ref to track current country/year for hydro fetch to prevent stale updates
  const hydroFetchRef = React.useRef({ countryIso: null, year: null });
  const [nuclearProfile, setNuclearProfile] = useState(null);
  const [nuclearCapacityGW, setNuclearCapacityGW] = useState(null);
  // Dispatchable & Storage applied capacities (simulation)
  const [coalCapacityGW, setCoalCapacityGW] = useState(null);
  const [gasCapacityGW, setGasCapacityGW] = useState(null);
  const [oilCapacityGW, setOilCapacityGW] = useState(null);
  const [bioCapacityGW, setBioCapacityGW] = useState(null);
  const [geoCapacityGW, setGeoCapacityGW] = useState(null);
  const [storageCapacityGW, setStorageCapacityGW] = useState(null);
  const [storageDurationH, setStorageDurationH] = useState(DEFAULT_STORAGE_DURATION_H);
  const [pumpedStorageCapacityGW, setPumpedStorageCapacityGW] = useState(null);
  const [pumpedStorageDurationH, setPumpedStorageDurationH] = useState(DEFAULT_PUMPED_STORAGE_DURATION_H);
  const storageEfficiency = 0.85; // Fixed round-trip efficiency
  // Hydro monthly budgets (for frontend shaping)
  const [hydroMonthlyBudgetMWh, setHydroMonthlyBudgetMWh] = useState(null); // [12] array
  const [hydroIrenaCapacityGW, setHydroIrenaCapacityGW] = useState(null);
  // Must-run CF from database (null = data unavailable → must-run = 0)
  const [mustRunCF, setMustRunCF] = useState(null); // { coal: { utilization_factor }, bioenergy: { ... }, gas: { ... }, oil: { ... } }
  const methodologyAccordionRef = useRef(null);
  const hourlyChartSectionRef = useRef(null);
  // Weather Year state
  // `weatherYear` drives the DEMAND profile (/generation-profile).
  // `supplyWeatherYear` drives the SUPPLY-side hourly APIs (Solar/Wind/WindOff).
  const initialWyFromUrl =
    parsedShareScenario && Number.isFinite(parsedShareScenario.weatherYear)
      ? pickInitialDemandWeatherYearFromScenario(parsedShareScenario.weatherYear)
      : parsedVedaLookup && Number.isFinite(parsedVedaLookup.weatherYear)
        ? pickInitialDemandWeatherYearFromScenario(parsedVedaLookup.weatherYear)
        : 2013;
  const initialSwyFromUrl =
    parsedShareScenario && Number.isFinite(parsedShareScenario.supplyWeatherYear)
      ? pickInitialSupplyWeatherYearFromScenario(parsedShareScenario.supplyWeatherYear)
      : parsedVedaLookup && Number.isFinite(parsedVedaLookup.supplyWeatherYear)
        ? pickInitialSupplyWeatherYearFromScenario(parsedVedaLookup.supplyWeatherYear)
        : 2013;
  const [weatherYear, setWeatherYear] = useState(initialWyFromUrl);
  const weatherYearRef = React.useRef(initialWyFromUrl);
  const [availableWeatherYears, setAvailableWeatherYears] = useState([]);
  const availableWeatherYearsRef = React.useRef(availableWeatherYears);
  availableWeatherYearsRef.current = availableWeatherYears;
  const [weatherYearsLoading, setWeatherYearsLoading] = useState(false);
  const [weatherYearsError, setWeatherYearsError] = useState(null);
  const [supplyWeatherYear, setSupplyWeatherYear] = useState(initialSwyFromUrl);
  const supplyWeatherYearRef = React.useRef(initialSwyFromUrl);

  // Country-specific maximum installable capacity (GW) for renewable inputs.
  // Source: vervestacks.renewable_zones_{solar,wind_onshore,wind_offshore}
  // Used to validate Solar / WindOn / WindOff inputs and gate the Generate button.
  const [capacityLimits, setCapacityLimits] = useState({
    solar: null,
    windOn: null,
    windOff: null
  });
  // Ref mirror so fetchCapacityData can read latest limits without being in useCallback
  // deps (would recreate the callback and retrigger the fetch effect in a loop).
  const capacityLimitsRef = React.useRef(capacityLimits);
  capacityLimitsRef.current = capacityLimits;
  const [capacityLimitsLoading, setCapacityLimitsLoading] = useState(false);
  const [capacityLimitsError, setCapacityLimitsError] = useState(null);
  // Token used to ignore stale responses when the country switches mid-flight.
  const capacityLimitsRequestRef = React.useRef(0);
  /** VO lookup: do not clamp/block solar/wind to resource-map potential. */
  const isVoLookupMode = Boolean(parsedVedaLookup && !parsedShareScenario);
  const renewableCapacityLimits = isVoLookupMode
    ? { solar: null, windOn: null, windOff: null }
    : capacityLimits;

  // Zoom synchronization state
  // Tooltip toggle for Hourly Generation & Demand chart (default: on)
  const tooltipEnabledRef = useRef(true); // Ref for formatter - TooltipSwitch owns state to avoid parent re-render
  const chartOverlayRef = useRef(null);
  // Processing log lives in ProcessingLogHost so log updates do not re-render this chart tree.
  const processingLogRef = useRef(null);

  // Capacity Inputs card: single Go button — track last applied values for dirty detection
  const [lastAppliedCapacityInputs, setLastAppliedCapacityInputs] = useState({
    solar: '', wind: '', windOff: '', nuclear: '', hydro: ''
  });
  const lastAppliedCapacityInputsRef = React.useRef(lastAppliedCapacityInputs);
  lastAppliedCapacityInputsRef.current = lastAppliedCapacityInputs;
  const [capacitySubmitLoading, setCapacitySubmitLoading] = useState(false);
  // Dispatchable & Storage card: single Go — track last applied values for dirty detection
  const [lastAppliedDispatchableInputs, setLastAppliedDispatchableInputs] = useState({
    bio: '',
    coal: '',
    geo: '',
    gas: '',
    oil: '',
    storage: '',
    duration: String(DEFAULT_STORAGE_DURATION_H),
    pumpedStorage: '',
    pumpedDuration: String(DEFAULT_PUMPED_STORAGE_DURATION_H)
  });
  /** @type {[Record<string, string>, Function]} last applied VO sub-tech GW drafts */
  const [lastAppliedVoSubtech, setLastAppliedVoSubtech] = useState({});
  /** @type {[Record<string, number>|null, Function]} applied overrides for merit-order caps */
  const [voSubtechCapacityApplied, setVoSubtechCapacityApplied] = useState(null);
  const [dispatchableStorageSubmitLoading, setDispatchableStorageSubmitLoading] = useState(false);
  const unifiedGenerateInFlightRef = useRef(false);
  // Collapsible cards: Capacity Inputs and Dispatchable & Storage (both expanded by default)
  const mobilePanelsRef = useRef(null);
  const desktopInputsSidebarRef = useRef(null);
  const desktopRightPanelRef = useRef(null);
  const leftPanelExpandedRef = useRef(
    typeof window === 'undefined' || !window.matchMedia(HOURLY_CHART_COMPACT_MQ).matches
  );
  const leftPanelInputCardRef = useRef(null);
  const demandInputAnchorRef = useRef(null);

  const [isCompactViewport, setIsCompactViewport] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(HOURLY_CHART_COMPACT_MQ).matches
  );
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(HOURLY_CHART_MOBILE_MQ).matches
  );
  const isCompactViewportRef = useRef(isCompactViewport);
  const prevCompactViewportRef = useRef(isCompactViewport);
  isCompactViewportRef.current = isCompactViewport;
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const compactMq = window.matchMedia(HOURLY_CHART_COMPACT_MQ);
    const mobileMq = window.matchMedia(HOURLY_CHART_MOBILE_MQ);
    const onChange = () => {
      setIsCompactViewport(compactMq.matches);
      setIsMobileViewport(mobileMq.matches);
    };
    compactMq.addEventListener('change', onChange);
    mobileMq.addEventListener('change', onChange);
    onChange();
    return () => {
      compactMq.removeEventListener('change', onChange);
      mobileMq.removeEventListener('change', onChange);
    };
  }, []);

  useEffect(() => {
    const wasCompact = prevCompactViewportRef.current;
    prevCompactViewportRef.current = isCompactViewport;
    if (wasCompact === isCompactViewport) return;
    mobilePanelsRef.current?.closePanels();
    if (isCompactViewport) {
      desktopInputsSidebarRef.current?.collapse();
      desktopRightPanelRef.current?.collapse();
    } else {
      desktopInputsSidebarRef.current?.expand();
      desktopRightPanelRef.current?.expand();
    }
  }, [isCompactViewport]);

  /** Updates left-panel hover mirror inside Inputs panel only (avoids full chart re-render). */
  const pushLeftPanelTooltipData = useCallback((payload) => {
    inputsPanelRef.current?.setHoverTooltip?.(payload);
  }, []);

  const handleLeftPanelExpandedChange = useCallback((expanded) => {
    leftPanelExpandedRef.current = expanded;
    if (!expanded) {
      inputsPanelRef.current?.setHoverTooltip?.(null);
    }
  }, []);

  const getChangedCapacityKeysFromRef = useCallback(
    () => getChangedCapacityKeys(inputsLiveRef.current, lastAppliedCapacityInputs),
    [lastAppliedCapacityInputs]
  );

  const getChangedDispatchableKeysFromRef = useCallback(
    () => getChangedDispatchableKeys(inputsLiveRef.current, lastAppliedDispatchableInputs),
    [lastAppliedDispatchableInputs]
  );

  // Logging — delegate to ProcessingLogHost (isolated re-renders).
  const addLog = useCallback((message, source = 'frontend') => {
    processingLogRef.current?.addLog(message, source);
  }, []);

  const addLogs = useCallback((messages, source = 'frontend') => {
    processingLogRef.current?.addLogs(messages, source);
  }, []);

  const addErrorLog = useCallback((message) => {
    processingLogRef.current?.addErrorLog(message);
  }, []);

  useEffect(() => {
    if (!parsedVedaLookup || parsedShareScenario) {
      vedaOverlayScenarioRef.current = null;
      setVedaEngineResult(null);
      vedaEngineResultRef.current = null;
      setVedaEngineTrace(null);
      setVreProfileTraces(null);
      setVedaFetchError(null);
      setVedaFetchLoading(false);
      setVedaOverlayReady(true);
      vedaReportProfileFetchedRef.current = false;
      return undefined;
    }

    let cancelled = false;
    setVedaOverlayReady(false);
    setVedaFetchLoading(true);
    setVedaFetchError(null);
    setVedaTracePanelDismissed(false);
    setVedaGapsBannerDismissed(false);
    vedaOverlayScenarioRef.current = null;
    vedaReportProfileFetchedRef.current = false;
    setChartData(null);

    (async () => {
      try {
        addLog('Fetching VedaOnline capacity mix (engine)…');
        const result = await vedaonlineAPI.getCapacityMix(parsedVedaLookup);
        if (cancelled) return;

        if (!result?.success) {
          throw new Error(result?.error || 'Capacity mix engine failed');
        }

        setVedaEngineResult(result);
        vedaEngineResultRef.current = result;
        setVedaEngineTrace(result.trace || null);

        if (result.gaps?.degraded) {
          const msg = formatDegradedMessage(result.meta.degradedReasons || result.gaps?.degradedReasons);
          addLog(`VedaOnline partial data: ${msg}`, 'backend');
        }

        const rawScenario = buildVedaRawScenarioFromCapacityMix(result, parsedVedaLookup);
        const mappingRows = await ensureSimulatorTechMappingLoaded();
        if (!mappingRows) {
          throw new Error('Could not load technology mapping');
        }

        const { mapped, unmapped } = mapRawCapacityToSimulatorGws(
          rawScenario.capacity,
          mappingRows
        );
        const flexCatalog =
          voFlexCatalog || (await prefetchVoFlexTechCatalog()) || null;
        if (flexCatalog && !voFlexCatalog) setVoFlexCatalog(flexCatalog);
        setUnmappedTechs(
          filterUnmappedTechsCoveredByFlexCatalog(
            unmapped,
            result?.report?.capacityByTech,
            flexCatalog
          )
        );
        setScenarioUrlSpecifiedKeys(buildUrlSpecifiedKeysFromRaw(rawScenario, mapped));
        vedaOverlayScenarioRef.current = applyRawFossilZeroDefaults(
          rawMappedToNativePayload(rawScenario, mapped)
        );

        if (rawScenario.demandTwh !== undefined) {
          const demandStr = String(rawScenario.demandTwh);
          demandTwhRef.current = demandStr;
          applyInputsPanelSeed({ demandTwhInput: demandStr, demandTwhError: '' });
          setLastAppliedDemandTwhInput(demandStr);
          skipDemandAutofillFromChartDataRef.current = true;
        }

        addLog(
          `VedaOnline capacity loaded (schema: ${result.meta?.resultSchemaName || '—'}${
            rawScenario.demandTwh !== undefined
              ? `, demand: ${rawScenario.demandTwh} TWh`
              : ''
          })`
        );
        setVedaOverlayReady(true);
      } catch (err) {
        if (cancelled) return;
        const msg = err?.response?.data?.error || err?.message || 'VedaOnline capacity fetch failed';
        setVedaFetchError(msg);
        addErrorLog(msg);
        toast.error(msg);
        setVedaOverlayReady(true);
      } finally {
        if (!cancelled) setVedaFetchLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [parsedVedaLookup, parsedShareScenario, addLog, addErrorLog, applyInputsPanelSeed]);

  const addSeparator = useCallback(() => {
    processingLogRef.current?.addSeparator();
  }, []);

  const setIsProcessing = useCallback((value) => {
    processingLogRef.current?.setIsProcessing(value);
  }, []);

  const setHasError = useCallback((value) => {
    processingLogRef.current?.setHasError(value);
  }, []);

  const showLatestLog = useCallback(() => {
    processingLogRef.current?.showLatest();
  }, []);

  const hourlyDataReadyRef = useRef(false);
  const scheduleRevealHourlyChartLayoutRef = useRef(null);
  const restoreHourlyChartInteractionStateRef = useRef(null);
  /** Skip duplicate reveal runs (same chart layout signature). */
  const hourlyLayoutRevealedSignatureRef = useRef(null);
  // Sidebar generation summaries (GW)
  const solarAvgGW = useMemo(() => {
    if (!Array.isArray(solarProfile) || solarProfile.length === 0) return null;
    const sumMW = solarProfile.reduce((s, v) => s + (typeof v === 'number' ? v : 0), 0);
    return (sumMW / solarProfile.length) / 1000;
  }, [solarProfile]);
  const solarPeakGW = useMemo(() => {
    if (!Array.isArray(solarProfile) || solarProfile.length === 0) return null;
    return Math.max(...solarProfile) / 1000;
  }, [solarProfile]);
  const windAvgGW = useMemo(() => {
    if (!Array.isArray(windProfile) || windProfile.length === 0) return null;
    const sumMW = windProfile.reduce((s, v) => s + (typeof v === 'number' ? v : 0), 0);
    return (sumMW / windProfile.length) / 1000;
  }, [windProfile]);
  const windPeakGW = useMemo(() => {
    if (!Array.isArray(windProfile) || windProfile.length === 0) return null;
    return Math.max(...windProfile) / 1000;
  }, [windProfile]);
  const nuclearAvgGW = useMemo(() => {
    if (!Array.isArray(nuclearProfile) || nuclearProfile.length === 0) return null;
    // Nuclear profile is already in MW, convert to GW
    const sumMW = nuclearProfile.reduce((s, v) => s + (typeof v === 'number' ? v : 0), 0);
    return (sumMW / nuclearProfile.length) / 1000;
  }, [nuclearProfile]);
  const nuclearPeakGW = useMemo(() => {
    if (!Array.isArray(nuclearProfile) || nuclearProfile.length === 0) return null;
    // Nuclear profile is already in MW, convert to GW
    return Math.max(...nuclearProfile) / 1000;
  }, [nuclearProfile]);

  /**
   * VO per-tech flex stack — all classifiable flex techs (catalog overlay for labels/parent).
   * Missing Avg_cost_elec → matched null → legacy bio/coal/gas/oil lumps (new techs stay in parent GW).
   */
  const voFlexDispatchFiltered = useMemo(() => {
    if (!parsedVedaLookup || parsedShareScenario) {
      return { matched: null, unmapped: [] };
    }
    const report = vedaEngineResult?.report;
    if (!report?.dispatchAvailable) {
      return { matched: null, unmapped: [] };
    }
    const stack = Array.isArray(report.flexDispatchOrder) ? report.flexDispatchOrder : null;
    if (!stack?.length) {
      return { matched: null, unmapped: [] };
    }
    const normalized = stack
      .filter((row) => row && row.capacityGw > 0 && row.fuel && ['bio', 'coal', 'gas', 'oil'].includes(row.fuel))
      .map((row) => ({
        tech: String(row.tech || ''),
        techByFuel: row.techByFuel ?? null,
        fuel: row.fuel,
        capacityGw: Number(row.capacityGw) || 0,
        productionTwh: Number.isFinite(row.productionTwh) ? row.productionTwh : null,
        costPerMwh: Number.isFinite(row.costPerMwh) ? row.costPerMwh : Infinity,
        lastResort: Boolean(row.lastResort),
      }))
      .filter((row) => row.tech && row.capacityGw > 0 && Number.isFinite(row.costPerMwh));

    return enrichFlexDispatchStack(normalized, voFlexCatalog);
  }, [parsedVedaLookup, parsedShareScenario, vedaEngineResult?.report, voFlexCatalog]);

  const voFlexDispatchStack = useMemo(() => {
    const matched = voFlexDispatchFiltered.matched?.length
      ? voFlexDispatchFiltered.matched
      : null;
    if (!matched) return null;
    return applyVoSubtechCapacityOverrides(matched, voSubtechCapacityApplied);
  }, [voFlexDispatchFiltered.matched, voSubtechCapacityApplied]);

  const voUnmappedFlex = useMemo(() => {
    if (!parsedVedaLookup || parsedShareScenario) return [];
    const fromStack = voFlexDispatchFiltered.unmapped || [];
    const fromCap = findUnmappedFlexCapacity(
      vedaEngineResult?.report?.capacityByTech,
      voFlexCatalog,
      classifyFlexFuel
    );
    // Prefer stack unmapped; merge capacity-only misses by tech key
    const seen = new Set(
      fromStack.map((r) => `${r.tech}\0${r.techByFuel ?? ''}`)
    );
    const extra = fromCap.filter((r) => !seen.has(`${r.tech}\0${r.techByFuel ?? ''}`));
    return [...fromStack, ...extra];
  }, [
    parsedVedaLookup,
    parsedShareScenario,
    voFlexDispatchFiltered.unmapped,
    vedaEngineResult?.report?.capacityByTech,
    voFlexCatalog,
  ]);

  // =====================================================================
  // HOURLY DISPATCH SIMULATION
  // Must-run baseload → RE → pumped + battery storage → Hydro → Merit-order dispatch
  // Storage: discharge pumped first then battery; charge battery first then pumped.
  // =====================================================================
  const simulation = useMemo(
    () => runHourlyDispatchSimulation({
      hourlyProfile: chartData?.hourlyProfile,
      solarProfile,
      windProfile,
      windOffProfile,
      nuclearProfile,
      bioCapacityGW,
      coalCapacityGW,
      geoCapacityGW,
      gasCapacityGW,
      oilCapacityGW,
      nuclearCapacityGW,
      solarCapacityGW,
      windCapacityGW,
      windOffCapacityGW,
      storageCapacityGW,
      storageDurationH,
      storageEfficiency,
      pumpedStorageCapacityGW,
      pumpedStorageDurationH,
      hydroCapacityGW,
      hydroMonthlyBudgetMWh,
      hydroIrenaCapacityGW,
      simulationYear: formData?.year || chartData?.year,
      mustRunCF,
      voFlexDispatchStack,
    }),
    [
      chartData?.hourlyProfile,
      solarProfile,
      windProfile,
      windOffProfile,
      nuclearProfile,
      nuclearCapacityGW,
      solarCapacityGW,
      windCapacityGW,
      windOffCapacityGW,
      bioCapacityGW,
      coalCapacityGW,
      geoCapacityGW,
      gasCapacityGW,
      oilCapacityGW,
      storageCapacityGW,
      storageDurationH,
      storageEfficiency,
      pumpedStorageCapacityGW,
      pumpedStorageDurationH,
      hydroCapacityGW,
      hydroMonthlyBudgetMWh,
      hydroIrenaCapacityGW,
      formData?.year,
      chartData?.year,
      mustRunCF,
      voFlexDispatchStack,
    ]
  );

  // Alias for backward compatibility
  const balanceMetrics = simulation?.balanceMetrics || null;
  const fuelUtilization = simulation?.fuelUtilization || null;
  const topMaxShortageEvents = balanceMetrics?.topMaxShortageEvents ?? [];
  const [maxShortageNavIndex, setMaxShortageNavIndex] = useState(0);
  /** Mobile chart header: Max Shortage nav shown only after user clicks date in System Balance. */
  const [maxShortageChartNavVisible, setMaxShortageChartNavVisible] = useState(false);
  const clampedMaxShortageNavIndex =
    topMaxShortageEvents.length > 0
      ? Math.min(maxShortageNavIndex, topMaxShortageEvents.length - 1)
      : 0;

  useEffect(() => {
    setMaxShortageNavIndex(0);
    setMaxShortageChartNavVisible(false);
  }, [simulation]);

  const voCapacityByFuel = useMemo(() => {
    if (!parsedVedaLookup || parsedShareScenario) return null;
    const rows = vedaEngineResult?.report?.capacityByTech;
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const grouped = groupCapacityByFlexFuel(rows, voFlexCatalog);
    const hasAny = Object.values(grouped).some((list) => list.length > 0);
    return hasAny ? grouped : null;
  }, [
    parsedVedaLookup,
    parsedShareScenario,
    vedaEngineResult?.report?.capacityByTech,
    voFlexCatalog,
  ]);

  // Seed VO sub-fuel drafts when catalog-grouped capacity arrives / changes.
  const voCapSeedKey = useMemo(() => {
    if (!voCapacityByFuel) return '';
    return ['bio', 'coal', 'gas', 'oil']
      .map((f) =>
        (voCapacityByFuel[f] || [])
          .map((r) => `${r.techId}:${Number(r.capacityGw) || 0}`)
          .join(',')
      )
      .join('|');
  }, [voCapacityByFuel]);

  useEffect(() => {
    if (!voCapSeedKey || !voCapacityByFuel) {
      setVoSubtechCapacityApplied(null);
      setLastAppliedVoSubtech({});
      applyInputsPanelSeed({ voSubtechByTechId: {} });
      return;
    }
    const seeded = seedVoSubtechDraftFromCapacityByFuel(
      voCapacityByFuel,
      formatCapacityGwInput
    );
    const bioGw = formatCapacityGwInput(
      sumVoSubtechDraftForFuel(voCapacityByFuel, 'bio', seeded)
    );
    const coalGw = formatCapacityGwInput(
      sumVoSubtechDraftForFuel(voCapacityByFuel, 'coal', seeded)
    );
    const gasGw = formatCapacityGwInput(
      sumVoSubtechDraftForFuel(voCapacityByFuel, 'gas', seeded)
    );
    const oilGw = formatCapacityGwInput(
      sumVoSubtechDraftForFuel(voCapacityByFuel, 'oil', seeded)
    );
    applyInputsPanelSeed({
      voSubtechByTechId: seeded,
      bioInputGW: bioGw,
      coalInputGW: coalGw,
      gasInputGW: gasGw,
      oilInputGW: oilGw,
    });
    setLastAppliedVoSubtech({ ...seeded });
    setVoSubtechCapacityApplied(parseVoSubtechDraftToApplied(seeded));
    setLastAppliedDispatchableInputs((prev) => ({
      ...prev,
      bio: bioGw,
      coal: coalGw,
      gas: gasGw,
      oil: oilGw,
    }));
  }, [voCapSeedKey, voCapacityByFuel, applyInputsPanelSeed]);

  const voGenByFuel = useMemo(() => {
    if (!parsedVedaLookup || parsedShareScenario) return null;
    if (simulation?.dispatchMode !== 'vo-report-tech') return null;
    const rows = simulation?.flexDispatchOrder;
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const grouped = groupSimGenByFlexFuel(rows, voFlexCatalog);
    const hasAny = Object.values(grouped).some((list) => list.length > 0);
    return hasAny ? grouped : null;
  }, [
    parsedVedaLookup,
    parsedShareScenario,
    simulation?.dispatchMode,
    simulation?.flexDispatchOrder,
    voFlexCatalog,
  ]);

  const leftPanelGenerationRowDefs = useMemo(() => ([
    { label: 'Hydro', colorKey: 'hydro' },
    { label: 'Solar', colorKey: 'solar' },
    { label: 'WindOn', colorKey: 'windon' },
    { label: 'WindOff', colorKey: 'windoff' },
    { label: 'Pumped Discharge', colorKey: 'pumped_storage', fallback: '#0080ff' },
    { label: 'Battery Discharge', colorKey: 'storage', fallback: '#9B59B6' },
    { label: 'Bio (dispatch)', colorKey: 'bioenergy' },
    { label: 'Coal (dispatch)', colorKey: 'coal' },
    { label: 'Gas', colorKey: 'gas' },
    { label: 'Oil', colorKey: 'oil' },
    { label: 'Geo (must-run)', colorKey: 'geothermal' },
    { label: 'Bio (must-run)', colorKey: 'bioenergy' },
    { label: 'Coal (must-run)', colorKey: 'coal' },
    { label: 'Nuclear', colorKey: 'nuclear' }
  ]), []);

  /** Tooltip / left-panel hover rows follow chart stack: sub-techs when expanded. */
  const hourlyTooltipGenerationRowDefs = useMemo(() => {
    const parentToFuel = {
      'Bio (dispatch)': 'bio',
      'Coal (dispatch)': 'coal',
      Gas: 'gas',
      Oil: 'oil',
    };
    if (simulation?.dispatchMode !== 'vo-report-tech') {
      return leftPanelGenerationRowDefs;
    }
    const byFuel = { bio: [], coal: [], gas: [], oil: [] };
    for (const row of simulation.flexDispatchOrder || []) {
      if (row?.fuel && byFuel[row.fuel]) byFuel[row.fuel].push(row);
    }
    const out = [];
    for (const def of leftPanelGenerationRowDefs) {
      const fuel = parentToFuel[def.label];
      if (!fuel) {
        out.push(def);
        continue;
      }
      const kids = byFuel[fuel];
      if (hasExpandableVoSubfuels(kids) && voFlexFuelCollapsed[fuel] === false) {
        const baseColor = getFuelColor(def.colorKey) || def.fallback || '#7F8C8D';
        kids.forEach((kid) => {
          const color = voFlexSubfuelColor(baseColor, fuel, kid.techId);
          out.push({
            label: kid.label || kid.tech,
            colorKey: def.colorKey,
            fallback: color,
            color,
          });
        });
      } else {
        out.push(def);
      }
    }
    return out;
  }, [
    leftPanelGenerationRowDefs,
    simulation?.dispatchMode,
    simulation?.flexDispatchOrder,
    voFlexFuelCollapsed,
  ]);
  // Calculate Hydro Avg and Peak from simulation data
  const hydroAvgGW = useMemo(() => {
    const hydroArr = simulation?.hydroSim;
    if (!Array.isArray(hydroArr) || hydroArr.length === 0) return null;
    // Hydro profile is already in MW, convert to GW
    const sumMW = hydroArr.reduce((s, v) => s + (typeof v === 'number' ? v : 0), 0);
    return (sumMW / hydroArr.length) / 1000;
  }, [simulation]);
  const hydroPeakGW = useMemo(() => {
    const hydroArr = simulation?.hydroSim;
    if (!Array.isArray(hydroArr) || hydroArr.length === 0) return null;
    // Hydro profile is already in MW, convert to GW
    return Math.max(...hydroArr) / 1000;
  }, [simulation]);

  // Fetch solar zones (shapes) for current ISO once per ISO
  useEffect(() => {
    let cancelled = false;
    const fetchZones = async () => {
      try {
        if (!countryIso) return;
        const resp = await renewablePotentialAPI.getSolarZones(countryIso);
        if (!resp?.success || !resp.data?.grid_data) return;
        const features = resp.data.grid_data
          .filter(r => r?.geometry)
          .map(r => ({
            type: 'Feature',
            properties: { grid_cell: r.grid_cell },
            geometry: r.geometry
          }));
        if (!cancelled) setSolarZonesGeoJSON({ type: 'FeatureCollection', features });
      } catch (e) {
        if (!cancelled) setSolarZonesGeoJSON(null);
      }
    };
    fetchZones();
    return () => { cancelled = true; };
  }, [countryIso]);

  // Fetch wind zones (onshore) for current ISO
  useEffect(() => {
    let cancelled = false;
    const fetchZones = async () => {
      try {
        if (!countryIso) return;
        const resp = await renewablePotentialAPI.getWindZones(countryIso, 'onshore');
        if (!resp?.success || !resp.data?.grid_data) return;
        const features = resp.data.grid_data
          .filter(r => r?.geometry)
          .map(r => ({
            type: 'Feature',
            properties: { grid_cell: r.grid_cell },
            geometry: r.geometry
          }));
        if (!cancelled) setWindZonesGeoJSON({ type: 'FeatureCollection', features });
      } catch (e) {
        if (!cancelled) setWindZonesGeoJSON(null);
      }
    };
    fetchZones();
    return () => { cancelled = true; };
  }, [countryIso]);

  // Fetch wind zones (offshore) for current ISO
  useEffect(() => {
    let cancelled = false;
    const fetchZones = async () => {
      try {
        if (!countryIso) return;
        const resp = await renewablePotentialAPI.getWindZones(countryIso, 'offshore');
        if (!resp?.success || !resp.data?.grid_data) return;
        const features = resp.data.grid_data
          .filter(r => r?.geometry)
          .map(r => ({
            type: 'Feature',
            properties: { grid_cell: r.grid_cell },
            geometry: r.geometry
          }));
        if (!cancelled) setWindOffZonesGeoJSON({ type: 'FeatureCollection', features });
      } catch (e) {
        if (!cancelled) setWindOffZonesGeoJSON(null);
      }
    };
    fetchZones();
    return () => { cancelled = true; };
  }, [countryIso]);

  // Fetch country-specific maximum installable capacity (GW) for Solar /
  // WindOn / WindOff in parallel whenever the selected country changes.
  // - Resets state immediately on country switch so stale limits never gate
  //   the next country's inputs.
  // - Uses a request token to discard out-of-order responses.
  // - `Promise.allSettled` so a partial DB failure (e.g. wind table empty)
  //   does not prevent the other limits from being applied.
  useEffect(() => {
    if (!countryIso) {
      setCapacityLimits({ solar: null, windOn: null, windOff: null });
      setCapacityLimitsError(null);
      setCapacityLimitsLoading(false);
      return;
    }

    const requestId = capacityLimitsRequestRef.current + 1;
    capacityLimitsRequestRef.current = requestId;
    let cancelled = false;

    setCapacityLimits({ solar: null, windOn: null, windOff: null });
    setCapacityLimitsError(null);
    setCapacityLimitsLoading(true);

    const extractGw = (settled) => {
      if (settled.status !== 'fulfilled') return null;
      const payload = settled.value;
      if (!payload || payload.success === false) return null;
      const gw = Number(payload.limit_gw);
      return Number.isFinite(gw) ? gw : null;
    };

    (async () => {
      try {
        const [solarRes, windOnRes, windOffRes] = await Promise.allSettled([
          renewablePotentialAPI.getSolarCapacityLimit(countryIso),
          renewablePotentialAPI.getWindOnshoreCapacityLimit(countryIso),
          renewablePotentialAPI.getWindOffshoreCapacityLimit(countryIso)
        ]);
        if (cancelled || capacityLimitsRequestRef.current !== requestId) return;

        const next = {
          solar: extractGw(solarRes),
          windOn: extractGw(windOnRes),
          windOff: extractGw(windOffRes)
        };
        const allFailed = [solarRes, windOnRes, windOffRes].every(r => r.status === 'rejected');
        setCapacityLimits(next);
        setCapacityLimitsError(allFailed ? 'Failed to load capacity limits for this country.' : null);
      } catch (err) {
        if (cancelled || capacityLimitsRequestRef.current !== requestId) return;
        setCapacityLimitsError('Failed to load capacity limits for this country.');
      } finally {
        if (!cancelled && capacityLimitsRequestRef.current === requestId) {
          setCapacityLimitsLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [countryIso]);

  // Reload handlers
  const handleReloadTimeline = async () => {
    if (!countryIso || !formData.year) return;
    await handleSubmit();
  };

  const getHourlyProfileErrorMessage = useCallback((error, profileLabel) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.error || error?.response?.data?.detail || error?.response?.data?.message || error?.message || '';
    const normalized = String(detail).toLowerCase();

    if (status === 400 && normalized.includes('weatheryear')) {
      return 'Invalid weather year selected';
    }
    if ((status === 404 || status === 422) && normalized.includes('weather')) {
      return 'Weather data not available for selected year';
    }
    if (normalized.includes('weather data not available') || normalized.includes('no weather years found') || normalized.includes('weather year')) {
      return normalized.includes('invalid') ? 'Invalid weather year selected' : 'Weather data not available for selected year';
    }
    return `Failed to generate ${profileLabel} profile`;
  }, []);

  const vreProfileDegradedReasons = useMemo(
    () => collectVreProfileDegradedReasons(vreProfileTraces),
    [vreProfileTraces],
  );

  const getVoVreClusters = useCallback((vreKey) => {
    const clusters = vedaEngineResultRef.current?.vre?.[vreKey]?.clusters
      ?? vedaEngineResult?.vre?.[vreKey]?.clusters;
    return Array.isArray(clusters) ? clusters : [];
  }, [vedaEngineResult]);

  const recordVreProfileTrace = useCallback((techKey, response) => {
    if (!response?.trace && !response?.coverage) return;
    setVreProfileTraces((prev) => ({
      ...(prev || {}),
      [techKey]: {
        trace: response.trace,
        coverage: response.coverage || response.trace?.profile_coverage || null,
        status_messages: response.status_messages || [],
      },
    }));
    const coverageReasons = response.coverage?.degradedReasons
      || response.trace?.profile_coverage?.degradedReasons;
    if (coverageReasons?.length) {
      addLog(formatDegradedMessage(coverageReasons), 'backend');
    }
  }, [addLog]);

  const fetchVoVreProfileForTech = useCallback(async (technology, vreKey, capGw, label) => {
    const clusters = getVoVreClusters(vreKey);
    const cap = Number(capGw);
    if (!Number.isFinite(cap) || cap <= 0) {
      return {
        success: true,
        profile: Array(8760).fill(0),
        selected_cells: {},
        status_messages: [`${label}: zero capacity — empty profile`],
      };
    }
    if (!clusters.length) {
      return {
        success: true,
        profile: Array(8760).fill(0),
        selected_cells: {},
        status_messages: [
          `${label}: ${cap} GW in simulator but VO returned no clusters — hourly profile set to zero`,
        ],
        trace: {
          technology,
          clusters_requested: 0,
          profiles_found: 0,
          profiles_missing: [],
          warning: 'no_vo_clusters',
          combined_profile_stats: {
            total_capacity_gw: cap,
            peak_mw: 0,
            annual_mwh: 0,
            implied_cf: 0,
            formula: 'MW[h] = sum(cluster_gw * 1000 * cf[h])',
          },
        },
      };
    }
    const response = await generationProfileAPI.getVoVreProfile({
      isoCode: countryIso,
      technology,
      clusters,
      weatherYear: supplyWeatherYearRef.current,
    });
    recordVreProfileTrace(vreKey, response);
    return response;
  }, [getVoVreClusters, countryIso, recordVreProfileTrace]);

  const fetchSolarHourlyProfile = useCallback(async (cap) => {
    if (parsedVedaLookup && !parsedShareScenario) {
      return fetchVoVreProfileForTech('solar', 'solar', cap, 'Solar');
    }
    return generationProfileAPI.getSolarHourly(
      countryIso,
      formData.year,
      cap,
      supplyWeatherYearRef.current
    );
  }, [parsedVedaLookup, parsedShareScenario, fetchVoVreProfileForTech, countryIso, formData.year]);

  const fetchWindHourlyProfile = useCallback(async (cap) => {
    if (parsedVedaLookup && !parsedShareScenario) {
      return fetchVoVreProfileForTech('wind_onshore', 'windOn', cap, 'Onshore wind');
    }
    return generationProfileAPI.getWindHourly(
      countryIso,
      formData.year,
      cap,
      supplyWeatherYearRef.current
    );
  }, [parsedVedaLookup, parsedShareScenario, fetchVoVreProfileForTech, countryIso, formData.year]);

  const fetchWindOffHourlyProfile = useCallback(async (cap) => {
    if (parsedVedaLookup && !parsedShareScenario) {
      return fetchVoVreProfileForTech('wind_offshore', 'windOff', cap, 'Offshore wind');
    }
    return generationProfileAPI.getWindOffHourly(
      countryIso,
      formData.year,
      cap,
      supplyWeatherYearRef.current
    );
  }, [parsedVedaLookup, parsedShareScenario, fetchVoVreProfileForTech, countryIso, formData.year]);

  // Handler for Solar "Go" button
  const handleSolarGo = useCallback(async (fromBatch = false, capGwOverride) => {
    const raw = capGwOverride !== undefined && capGwOverride !== null
      ? capGwOverride
      : inputsLiveRef.current.solarInputGW;
    const cap = parseFloat(raw);
    if (isNaN(cap) || cap < 0) return;
    if (cap === 0) {
    setSolarLoading(true);
    if (!fromBatch) setIsProcessing(true);
    addLog('Clearing solar profile...');
    showLatestLog();
      requestAnimationFrame(() => {
        setSolarCapacityGW(0);
        setSolarProfile(Array(8760).fill(0));
        setSolarSelectedCells(null);
        setTimeout(() => { setSolarLoading(false); if (!fromBatch) setIsProcessing(false); }, 200);
      });
      return;
    }
    setSolarLoading(true);
    setSolarSelectedCells(null);
    if (!fromBatch) setIsProcessing(true);
    addLog(`Fetching solar hourly profile${parsedVedaLookup && !parsedShareScenario ? ' from VO clusters' : ' from grid cells'} (${cap} GW)...`);
    showLatestLog();
    try {
      const solarResp = await fetchSolarHourlyProfile(cap);
      if (solarResp?.success && Array.isArray(solarResp.profile)) {
        setSolarProfile(solarResp.profile);
        setSolarCapacityGW(cap);
        setSolarSelectedCells(solarResp.selected_cells || null);
        if (solarResp.status_messages?.length) solarResp.status_messages.forEach(msg => addLog(msg, 'backend'));
        const cellCount = solarResp.selected_cells ? Object.keys(solarResp.selected_cells).length : 0;
        addLog(
          parsedVedaLookup && !parsedShareScenario && vedaEngineResult?.vre?.solar?.clusters?.length
            ? `Solar profile loaded from ${cellCount} VO cluster(s)`
            : `Solar profile loaded from ${cellCount} grid cells`,
          'backend'
        );
      } else {
        const apiMsg = solarResp?.error || solarResp?.message || 'Solar profile endpoint returned no data';
        const msg = getHourlyProfileErrorMessage({ response: { data: { message: apiMsg } } }, 'solar');
        setSolarProfile(null);
        setSolarSelectedCells(null);
        addErrorLog(msg);
      }
    } catch (e) {
      console.error('❌ Error fetching solar hourly profile:', e);
      const msg = getHourlyProfileErrorMessage(e, 'solar');
      setSolarProfile(null);
      addErrorLog(msg);
    } finally {
      setSolarLoading(false);
      if (!fromBatch) setIsProcessing(false);
    }
  }, [countryIso, formData.year, supplyWeatherYear, parsedVedaLookup, parsedShareScenario, vedaEngineResult, fetchSolarHourlyProfile, addLog, addErrorLog, getHourlyProfileErrorMessage]);

  // Handler for Wind "Go" button
  const handleWindGo = useCallback(async (fromBatch = false, capGwOverride) => {
    const raw = capGwOverride !== undefined && capGwOverride !== null
      ? capGwOverride
      : inputsLiveRef.current.windInputGW;
    const cap = parseFloat(raw);
    if (isNaN(cap) || cap < 0) return;
    if (cap === 0) {
    setWindLoading(true);
    if (!fromBatch) setIsProcessing(true);
    addLog('Clearing wind profile...');
    showLatestLog();
      requestAnimationFrame(() => {
        setWindCapacityGW(0);
        setWindProfile(Array(8760).fill(0));
        setWindSelectedCells(null);
        setTimeout(() => { setWindLoading(false); if (!fromBatch) setIsProcessing(false); }, 200);
      });
      return;
    }
    setWindLoading(true);
    setWindSelectedCells(null);
    if (!fromBatch) setIsProcessing(true);
    addLog(`Fetching wind (onshore) hourly profile${parsedVedaLookup && !parsedShareScenario ? ' from VO clusters' : ' from grid cells'} (${cap} GW)...`);
    showLatestLog();
    try {
      const windResp = await fetchWindHourlyProfile(cap);
      if (windResp?.success && Array.isArray(windResp.profile)) {
        setWindProfile(windResp.profile);
        setWindCapacityGW(cap);
        setWindSelectedCells(windResp.selected_cells || null);
        if (windResp?.status_messages?.length) {
          windResp.status_messages.forEach((msg) => {
            if (msg.includes('no clusters') || msg.includes('missing')) addLog(msg, 'backend');
          });
        }
        const cellCount = windResp.selected_cells ? Object.keys(windResp.selected_cells).length : 0;
        addLog(
          parsedVedaLookup && !parsedShareScenario && vedaEngineResult?.vre?.windOn?.clusters?.length
            ? `Wind (onshore) profile loaded from ${cellCount} VO cluster(s)`
            : `Wind (onshore) profile loaded from ${cellCount} grid cells`,
          'backend'
        );
      } else {
        const apiMsg = windResp?.error || windResp?.message || 'Wind profile endpoint returned no data';
        const msg = getHourlyProfileErrorMessage({ response: { data: { message: apiMsg } } }, 'wind');
        setWindProfile(null);
        setWindSelectedCells(null);
        addErrorLog(msg);
      }
    } catch (e) {
      console.error('❌ Error fetching wind hourly profile:', e);
      const msg = getHourlyProfileErrorMessage(e, 'wind');
      setWindProfile(null);
      addErrorLog(msg);
    } finally {
      setWindLoading(false);
      if (!fromBatch) setIsProcessing(false);
    }
  }, [countryIso, formData.year, supplyWeatherYear, parsedVedaLookup, parsedShareScenario, vedaEngineResult, fetchWindHourlyProfile, addLog, addErrorLog, getHourlyProfileErrorMessage]);

  // Handler for Wind Offshore "Go" button
  const handleWindOffGo = useCallback(async (fromBatch = false, capGwOverride) => {
    const raw = capGwOverride !== undefined && capGwOverride !== null
      ? capGwOverride
      : inputsLiveRef.current.windOffInputGW;
    const cap = parseFloat(raw);
    if (isNaN(cap) || cap < 0) return;
    if (cap === 0) {
    setWindOffLoading(true);
    if (!fromBatch) setIsProcessing(true);
    addLog('Clearing wind (offshore) profile...');
    showLatestLog();
      requestAnimationFrame(() => {
        setWindOffCapacityGW(0);
        setWindOffProfile(Array(8760).fill(0));
        setWindOffSelectedCells(null);
        setTimeout(() => { setWindOffLoading(false); if (!fromBatch) setIsProcessing(false); }, 200);
      });
      return;
    }
    setWindOffLoading(true);
    setWindOffSelectedCells(null);
    if (!fromBatch) setIsProcessing(true);
    addLog(`Fetching wind (offshore) hourly profile${parsedVedaLookup && !parsedShareScenario ? ' from VO clusters' : ' from grid cells'} (${cap} GW)...`);
    showLatestLog();
    try {
      const windOffResp = await fetchWindOffHourlyProfile(cap);
      if (windOffResp?.success && Array.isArray(windOffResp.profile)) {
        setWindOffProfile(windOffResp.profile);
        setWindOffCapacityGW(cap);
        setWindOffSelectedCells(windOffResp.selected_cells || null);
        if (windOffResp.status_messages?.length) windOffResp.status_messages.forEach(msg => addLog(msg, 'backend'));
        const cellCount = windOffResp.selected_cells ? Object.keys(windOffResp.selected_cells).length : 0;
        addLog(
          parsedVedaLookup && !parsedShareScenario && vedaEngineResult?.vre?.windOff?.clusters?.length
            ? `Wind (offshore) profile loaded from ${cellCount} VO cluster(s)`
            : `Wind (offshore) profile loaded from ${cellCount} grid cells`,
          'backend'
        );
      } else {
        const apiMsg = windOffResp?.error || windOffResp?.message || 'Offshore wind profile endpoint returned no data';
        const msg = getHourlyProfileErrorMessage({ response: { data: { message: apiMsg } } }, 'offshore wind');
        setWindOffProfile(null);
        setWindOffSelectedCells(null);
        addErrorLog(msg);
      }
    } catch (e) {
      console.error('❌ Error fetching offshore wind hourly profile:', e);
      const msg = getHourlyProfileErrorMessage(e, 'offshore wind');
      setWindOffProfile(null);
      addErrorLog(msg);
    } finally {
      setWindOffLoading(false);
      if (!fromBatch) setIsProcessing(false);
    }
  }, [countryIso, formData.year, supplyWeatherYear, parsedVedaLookup, parsedShareScenario, vedaEngineResult, fetchWindOffHourlyProfile, addLog, addErrorLog, getHourlyProfileErrorMessage]);

  // Handler for Nuclear "Go" button
  const handleNuclearGo = useCallback(async (fromBatch = false, capGwOverride) => {
    const raw = capGwOverride !== undefined && capGwOverride !== null
      ? capGwOverride
      : inputsLiveRef.current.nuclearInputGW;
    const cap = parseFloat(raw);
    if (isNaN(cap) || cap < 0) return;
    if (cap === 0) {
    if (!fromBatch) setIsProcessing(true);
    addLog('Clearing nuclear profile...');
    showLatestLog();
      requestAnimationFrame(() => {
        setNuclearCapacityGW(0);
        applyInputsPanelSeed({ nuclearInputGW: formatCapacityGwInput(0) });
        setNuclearProfile(Array(8760).fill(0));
        setTimeout(() => {
          if (!fromBatch) setIsProcessing(false);
        }, 200);
      });
      return;
    }
    if (!fromBatch) setIsProcessing(true);
    addLog(`Fetching nuclear baseload profile (${cap} GW)...`);
    showLatestLog();
    try {
      const nuclearResp = await generationProfileAPI.getNuclearHourly(countryIso, cap);
      if (nuclearResp?.success && Array.isArray(nuclearResp.profile)) {
        setNuclearProfile(nuclearResp.profile);
        setNuclearCapacityGW(cap);
        applyInputsPanelSeed({ nuclearInputGW: formatCapacityGwInput(cap) });
        addLog('Nuclear baseload profile loaded successfully', 'backend');
      } else {
        setNuclearProfile(null);
        addErrorLog('Nuclear profile endpoint returned no data');
      }
    } catch (e) {
      console.error('❌ Error fetching nuclear hourly profile:', e);
      const msg = e?.response?.data?.message || e.message || 'Failed to fetch nuclear profile';
      setNuclearProfile(null);
      addErrorLog(msg);
    } finally {
      if (!fromBatch) setIsProcessing(false);
    }
  }, [countryIso, addLog, addErrorLog]);

  const handleHydroGo = useCallback((fromBatch = false, capGwOverride) => {
    const raw = capGwOverride !== undefined && capGwOverride !== null
      ? capGwOverride
      : inputsLiveRef.current.hydroInputGW;
    const cap = parseFloat(raw);
    if (isNaN(cap) || cap < 0) {
      toast.error('Please enter a valid hydro capacity (GW)');
      return;
    }
    if (!fromBatch) setIsProcessing(true);
    addLog(`Updating hydro capacity (${cap} GW)...`);
    showLatestLog();
    setHydroCapacityGW(cap);
    applyInputsPanelSeed({ hydroInputGW: formatCapacityGwInput(cap) });
    setTimeout(() => {
      if (!fromBatch) setIsProcessing(false);
    }, 200);
    // Simulation useMemo will automatically reshape hydro with the new capacity
  }, [addLog]);

  // Single "Go" for Capacity Inputs card: run only changed handlers sequentially, don't stop on error
  const handleCapacitySubmit = useCallback(async () => {
    const changedKeys = getChangedCapacityKeysFromRef();
    if (changedKeys.length === 0) return;

    const hydroDisabled = !chartData || !nuclearProfile || !solarProfile || !windProfile;
    const runnableKeys = changedKeys.filter((k) => {
      if (k !== 'hydro') return true;
      if (hydroDisabled) {
        addLog('Hydro skipped: other profiles must be loaded first.');
        return false;
      }
      return true;
    });
    if (runnableKeys.length === 0) return;

    const live = inputsLiveRef.current;
    const toApply = {
      solar: live.solarInputGW,
      wind: live.windInputGW,
      windOff: live.windOffInputGW,
      nuclear: live.nuclearInputGW,
      hydro: live.hydroInputGW
    };

    setCapacitySubmitLoading(true);
    setIsProcessing(true);

    try {
    // Let browser paint the loader before heavy updates and re-renders
    await waitForNextPaint();

    const order = ['solar', 'wind', 'windOff', 'nuclear', 'hydro'];
    const handlers = {
      solar: handleSolarGo,
      wind: handleWindGo,
      windOff: handleWindOffGo,
      nuclear: handleNuclearGo,
      hydro: handleHydroGo
    };

    for (const key of order) {
      if (!runnableKeys.includes(key)) continue;
      const val = parseFloat(toApply[key]);
      if (Number.isNaN(val) || val < 0) {
        if (key === 'hydro') toast.error('Please enter a valid hydro capacity (GW)');
        continue;
      }
      try {
        const fn = handlers[key];
        const capValue = toApply[key];
        if (key === 'hydro') fn(true, capValue);
        else await fn(true, capValue);
      } catch (e) {
        console.error(`Capacity submit error (${key}):`, e);
        addErrorLog(`${key}: ${e?.message || e}`);
      }
    }

    setLastAppliedCapacityInputs((prev) => {
      const next = { ...prev };
      changedKeys.forEach((k) => {
        const raw = toApply[k];
        const num = parseFloat(raw);
        next[k] = Number.isFinite(num) ? formatCapacityGwInput(num) : String(raw ?? '');
      });
      return next;
    });
  } finally {
    setCapacitySubmitLoading(false);
    setIsProcessing(false);
  }
  }, [
    getChangedCapacityKeysFromRef,
    chartData,
    nuclearProfile,
    solarProfile,
    windProfile,
    handleSolarGo,
    handleWindGo,
    handleWindOffGo,
    handleNuclearGo,
    handleHydroGo,
    addLog,
    addErrorLog
  ]);

  // Single "Go" for Dispatchable & Storage card: run only changed inputs sequentially, don't stop on error
  const handleDispatchableStorageSubmit = useCallback(async () => {
    const changedKeys = getChangedDispatchableKeysFromRef();
    const live = inputsLiveRef.current;
    const voSubtechChanged =
      getChangedVoSubtechTechIds(live.voSubtechByTechId, lastAppliedVoSubtech).length > 0;
    if (changedKeys.length === 0 && !voSubtechChanged) return;

    const toApply = {
      bio: live.bioInputGW,
      coal: live.coalInputGW,
      geo: live.geoInputGW,
      gas: live.gasInputGW,
      oil: live.oilInputGW,
      storage: live.storageInputGW,
      duration: live.storageDurationInput,
      pumpedStorage: live.pumpedStorageInputGW,
      pumpedDuration: live.pumpedStorageDurationInput
    };

    setDispatchableStorageSubmitLoading(true);
    setIsProcessing(true);

    try {
      // Let browser paint the loader before heavy updates and re-renders
      await waitForNextPaint();

      if (voSubtechChanged || changedKeys.some((k) => ['bio', 'coal', 'gas', 'oil'].includes(k))) {
        const draftMap = live.voSubtechByTechId || {};
        setVoSubtechCapacityApplied(parseVoSubtechDraftToApplied(draftMap));
        setLastAppliedVoSubtech({ ...draftMap });
        addLog('Updating VO sub-fuel capacities...');
        showLatestLog();
      }

      const order = ['bio', 'coal', 'geo', 'gas', 'oil', 'storage', 'duration', 'pumpedStorage', 'pumpedDuration'];
      const keysToApply = new Set(changedKeys);
      if (voSubtechChanged) {
        for (const k of ['bio', 'coal', 'gas', 'oil']) keysToApply.add(k);
      }
      for (const key of order) {
        if (!keysToApply.has(key)) continue;
        if (key === 'duration') {
          const duration = Math.max(1, parseInt(toApply.duration, 10) || DEFAULT_STORAGE_DURATION_H);
          setStorageDurationH(duration);
          applyInputsPanelSeed({ storageDurationInput: String(duration) });
          continue;
        }
        if (key === 'pumpedDuration') {
          const d = Math.min(
            48,
            Math.max(1, parseInt(toApply.pumpedDuration, 10) || DEFAULT_PUMPED_STORAGE_DURATION_H)
          );
          setPumpedStorageDurationH(d);
          applyInputsPanelSeed({ pumpedStorageDurationInput: String(d) });
          continue;
        }
        const raw = parseFloat(toApply[key]);
        const cap = Number.isNaN(raw) ? 0 : raw;
        if (cap < 0) {
          toast.error(
            key === 'pumpedStorage'
              ? 'Please enter a valid pumped storage capacity (GW)'
              : `Please enter a valid ${key} capacity (GW)`
          );
          continue;
        }
        try {
          if (key === 'bio') {
            addLog('Updating bioenergy capacity...');
            showLatestLog();
            setBioCapacityGW(cap || 0);
          } else if (key === 'coal') {
            addLog('Updating coal capacity...');
            showLatestLog();
            setCoalCapacityGW(cap || 0);
          } else if (key === 'geo') {
            addLog('Updating geothermal capacity...');
            showLatestLog();
            setGeoCapacityGW(cap || 0);
          } else if (key === 'gas') {
            addLog('Updating gas capacity...');
            showLatestLog();
            setGasCapacityGW(cap || 0);
          } else if (key === 'oil') {
            addLog('Updating oil capacity...');
            showLatestLog();
            setOilCapacityGW(cap || 0);
          } else if (key === 'storage') {
            addLog('Updating storage capacity...');
            showLatestLog();
            setStorageCapacityGW(cap || 0);
          } else if (key === 'pumpedStorage') {
            addLog('Updating pumped storage capacity...');
            showLatestLog();
            setPumpedStorageCapacityGW(cap || 0);
          }
        } catch (e) {
          console.error(`Dispatchable submit error (${key}):`, e);
          addErrorLog(`${key}: ${e?.message || e}`);
        }
      }

      setLastAppliedDispatchableInputs((prev) => {
        const next = { ...prev };
        for (const k of keysToApply) {
          if (k === 'duration') {
            const duration = Math.max(1, parseInt(toApply.duration, 10) || DEFAULT_STORAGE_DURATION_H);
            next[k] = String(duration);
          } else if (k === 'pumpedDuration') {
            const d = Math.min(
              48,
              Math.max(1, parseInt(toApply.pumpedDuration, 10) || DEFAULT_PUMPED_STORAGE_DURATION_H)
            );
            next[k] = String(d);
          } else {
            const raw = toApply[k];
            const num = parseFloat(raw);
            next[k] = Number.isFinite(num) ? formatCapacityGwInput(num) : String(raw ?? '');
          }
        }
        return next;
      });
    } finally {
      setDispatchableStorageSubmitLoading(false);
      setIsProcessing(false);
    }
  }, [
    getChangedDispatchableKeysFromRef,
    lastAppliedVoSubtech,
    applyInputsPanelSeed,
    setBioCapacityGW,
    setCoalCapacityGW,
    setGeoCapacityGW,
    setGasCapacityGW,
    setOilCapacityGW,
    setStorageCapacityGW,
    setPumpedStorageCapacityGW,
    setStorageDurationH,
    setPumpedStorageDurationH,
    addLog,
    addErrorLog,
    showLatestLog,
  ]);

  // Handler for Hydro "Reload" — simulation reshapes hydro automatically, this is a no-op
  const handleReloadHydro = () => {
    // Hydro is shaped by the simulation useMemo; trigger via hydroCapacityGW state
    if (hydroCapacityGW > 0) {
      setHydroCapacityGW(prev => prev); // Force re-render if needed
    }
  };

  // Stable fetch function for capacity; overwriteInputs false = supply weather year only
  // (hourly/profile APIs use last-applied committed capacities via refs, not raw inputs).
  const fetchCapacityData = useCallback(async (options = {}) => {
    if (!countryIso || !formData.year) return;
  
    const userOverwrite = options.overwriteInputs !== false;
    const overwrite = userOverwrite;

    const parseNum = (v) => {
      const n = parseFloat(v);
      return !isNaN(n) && n >= 0 ? n : undefined;
    };
  
    setCapacityLoading(true);
    setCapacityError(null);
    // Clear any prior global error so the chart can recover from a previous
    // failed fetch (e.g. unsupported Supply weather year) once the new
    // request completes successfully.
    setError(null);
    addLog("Fetching capacity data from database...");
  
    // Supply weather year-only refresh must not push raw form inputs into GW state:
    // edits / partial typing / invalid-but-not-yet-submitted values stay in input fields only.
  
    try {
      const response = await capacityAPI.getCapacityByFuel(
        countryIso,
        formData.year
      );
  
      if (!response.success) {
        throw new Error(response.message || "Failed to fetch capacity data");
      }
  
      addLog("Capacity data loaded successfully");
  
      const cap = response.capacity || {};
      let nuclearCap = cap.Nuclear?.capacity_gw ?? cap.nuclear?.capacity_gw;
      let windCap = cap.Windon?.capacity_gw ?? cap.wind?.capacity_gw;
      let solarCap = cap.Solar?.capacity_gw ?? cap.solar?.capacity_gw;
      let windOffCap = cap.Windoff?.capacity_gw ?? cap.windoff?.capacity_gw;
      let hydroCap = cap.Hydro?.capacity_gw ?? cap.hydro?.capacity_gw ?? 0;
      let gasCap = cap.Gas?.capacity_gw ?? cap['Natural Gas']?.capacity_gw ?? 0;
      let coalCap = cap.Coal?.capacity_gw ?? 0;
      let oilCap = cap.Oil?.capacity_gw ?? 0;
      let bioCap = cap.Bioenergy?.capacity_gw ?? 0;
      let geoCap = cap.Geothermal?.capacity_gw ?? 0;
      let storageCap = cap.Storage?.capacity_gw ?? 0;
      let pumpedCap =
        cap['Pumped Storage']?.capacity_gw ??
        cap.PumpedStorage?.capacity_gw ??
        cap.pumped_storage?.capacity_gw ??
        0;
      let batteryDurationHours = DEFAULT_STORAGE_DURATION_H;
      let pumpedDurationHours = DEFAULT_PUMPED_STORAGE_DURATION_H;

      if (overwrite) {
        const scenarioOverlay =
          parsedShareScenarioRef.current?.v === 1
            ? parsedShareScenarioRef.current
            : vedaOverlayScenarioRef.current?.v === 1
              ? vedaOverlayScenarioRef.current
              : null;

        if (scenarioOverlay) {
        let rawScenario = scenarioOverlay;

        if (isRawScenarioFormat(rawScenario)) {
          const mappingRows = await ensureSimulatorTechMappingLoaded();
          if (!mappingRows) {
            toast.error('Could not load technology mapping; using database capacities only.');
            rawScenario = null;
            setUnmappedTechs([]);
            setScenarioUrlSpecifiedKeys(new Set());
          } else {
            const { mapped, unmapped } = mapRawCapacityToSimulatorGws(
              rawScenario.capacity,
              mappingRows
            );
            setUnmappedTechs(unmapped);
            setScenarioUrlSpecifiedKeys(
              buildUrlSpecifiedKeysFromRaw(rawScenario, mapped)
            );
            rawScenario = applyRawFossilZeroDefaults(
              rawMappedToNativePayload(rawScenario, mapped)
            );
          }
        } else {
          setScenarioUrlSpecifiedKeys(
            buildUrlSpecifiedKeysFromNative(rawScenario)
          );
        }

        if (!rawScenario) {
          // raw mapping failed — skip scenario merge
        } else {
        const mergeInput = buildMergedScenarioInput(rawScenario, {
          weatherYear: weatherYearRef.current,
          supplyWeatherYear: supplyWeatherYearRef.current,
          solarGw: solarCap,
          windOnGw: windCap,
          windOffGw: windOffCap,
          nuclearGw: nuclearCap,
          hydroGw: hydroCap,
          coalGw: coalCap,
          gasGw: gasCap,
          oilGw: oilCap,
          bioGw: bioCap,
          geoGw: geoCap,
          batteryStorageGw: storageCap,
          batteryDurationHours,
          pumpedStorageGw: pumpedCap,
          pumpedDurationHours
        });
        const scenario = normalizeSimulatorScenario(
          mergeInput,
          parsedVedaLookupRef.current && !parsedShareScenarioRef.current
            ? { solar: null, windOn: null, windOff: null }
            : capacityLimitsRef.current,
          availableWeatherYearsRef.current
        );
        if (scenario) {
          nuclearCap = scenario.nuclearGw;
          windCap = scenario.windOnGw;
          solarCap = scenario.solarGw;
          windOffCap = scenario.windOffGw;
          hydroCap = scenario.hydroGw;
          coalCap = scenario.coalGw;
          gasCap = scenario.gasGw;
          oilCap = scenario.oilGw;
          bioCap = scenario.bioGw;
          geoCap = scenario.geoGw;
          storageCap = scenario.batteryStorageGw;
          pumpedCap = scenario.pumpedStorageGw;
          batteryDurationHours = scenario.batteryDurationHours;
          pumpedDurationHours = scenario.pumpedDurationHours;
          weatherYearRef.current = scenario.weatherYear;
          setWeatherYear(scenario.weatherYear);
          supplyWeatherYearRef.current = scenario.supplyWeatherYear;
          setSupplyWeatherYear(scenario.supplyWeatherYear);
          if (scenario.demandTwh !== undefined) {
            const demandStr = String(scenario.demandTwh);
            demandTwhRef.current = demandStr;
            applyInputsPanelSeed({ demandTwhInput: demandStr, demandTwhError: '' });
            setLastAppliedDemandTwhInput(demandStr);
          }
        }
        }
        }
      }
  
      const getValidWithinLimit = (value, limit) => {
        const parsed = parseNum(value);
        if (parsed === undefined) return undefined;
        if (!Number.isFinite(limit) || limit <= 0) return parsed;
        return parsed <= limit ? parsed : undefined;
      };

      const getCommittedRenewableEffective = (key, baseCap, limit) => {
        // Renewable Year change: use last Generate-applied values only (never raw edit box).
        const fromLastApplied = getValidWithinLimit(lastAppliedCapacityInputsRef.current?.[key], limit);
        if (fromLastApplied !== undefined) return fromLastApplied;

        const fromBase = getValidWithinLimit(baseCap, limit);
        if (fromBase !== undefined) return fromBase;

        if (Number.isFinite(limit) && limit > 0) return limit;
        return baseCap;
      };

      const limits =
        parsedVedaLookupRef.current && !parsedShareScenarioRef.current
          ? { solar: null, windOn: null, windOff: null }
          : capacityLimitsRef.current;

      const committedNuclear =
        parseNum(lastAppliedCapacityInputsRef.current?.nuclear) ?? nuclearCap;
      const committedHydro =
        parseNum(lastAppliedCapacityInputsRef.current?.hydro) ?? hydroCap;

      const effective = {
        nuclear: overwrite ? nuclearCap : committedNuclear,
        wind: overwrite
          ? windCap
          : getCommittedRenewableEffective('wind', windCap, limits.windOn),
        solar: overwrite
          ? solarCap
          : getCommittedRenewableEffective('solar', solarCap, limits.solar),
        windOff: overwrite
          ? windOffCap
          : getCommittedRenewableEffective('windOff', windOffCap, limits.windOff),
        hydro: overwrite ? hydroCap : committedHydro
      };
  
      /* ------------ Nuclear ------------ */
  
      if (typeof nuclearCap === "number") {
        setNuclearCapacityGW(effective.nuclear);
  
        if (overwrite) {
          applyInputsPanelSeed({
            nuclearInputGW: formatCapacityGwInput(nuclearCap),
          });
        }
  
        if (nuclearCap === 0) {
          setNuclearProfile(Array(8760).fill(0));
        }
  
        if (nuclearCap < 0) {
          setNuclearProfile(null);
        }
      }
  
      /* ------------ Wind ------------ */

      if (typeof windCap === "number" && windCap > 0) {
        setWindCapacityGW(effective.wind);

        if (overwrite) {
          applyInputsPanelSeed({
            windInputGW: formatCapacityGwInput(windCap),
          });
        }
        // Always mark loading when we're going to refetch the hourly profile
        // (both on initial overwrite and on weather-year-only changes)
        setWindLoading(true);
      } else if (windCap === 0) {
        setWindCapacityGW(0);
        setWindProfile(Array(8760).fill(0));
  
        if (overwrite) applyInputsPanelSeed({ windInputGW: '0' });
        setWindLoading(false);
      } else {
        setWindProfile(null);
        setWindLoading(false);
      }
  
      /* ------------ Solar ------------ */

      if (typeof solarCap === "number" && solarCap > 0) {
        setSolarCapacityGW(effective.solar);

        if (overwrite) {
          applyInputsPanelSeed({
            solarInputGW: formatCapacityGwInput(solarCap),
          });
        }
        setSolarLoading(true);
      } else if (solarCap === 0) {
        setSolarCapacityGW(0);
        setSolarProfile(Array(8760).fill(0));
  
        if (overwrite) applyInputsPanelSeed({ solarInputGW: '0' });
        setSolarLoading(false);
      } else {
        setSolarProfile(null);
        setSolarLoading(false);
      }
  
      /* ------------ Wind Offshore ------------ */

      if (typeof windOffCap === "number" && windOffCap > 0) {
        setWindOffCapacityGW(effective.windOff);

        if (overwrite) {
          applyInputsPanelSeed({
            windOffInputGW: formatCapacityGwInput(windOffCap),
          });
        }
        setWindOffLoading(true);
      } else if (windOffCap === 0) {
        setWindOffCapacityGW(0);
        setWindOffProfile(Array(8760).fill(0));
  
        if (overwrite) applyInputsPanelSeed({ windOffInputGW: '0' });
        setWindOffLoading(false);
      } else {
        setWindOffProfile(null);
        setWindOffLoading(false);
      }
  
      /* ------------ Hydro ------------ */
  
      if (hydroCap > 0 || effective.hydro > 0) {
        setHydroCapacityGW(effective.hydro);
  
        if (overwrite) {
          applyInputsPanelSeed({
            hydroInputGW: formatCapacityGwInput(hydroCap),
          });
        }
      }
  
      /* ------------ Dispatchables ------------ */
  
      if (overwrite) {
        setCoalCapacityGW(coalCap);
        setGasCapacityGW(gasCap);
        setOilCapacityGW(oilCap);
        setBioCapacityGW(bioCap);
        setGeoCapacityGW(geoCap);
        setPumpedStorageCapacityGW(pumpedCap);
        setPumpedStorageDurationH(pumpedDurationHours);
        setStorageCapacityGW(storageCap);
        setStorageDurationH(batteryDurationHours);
        applyInputsPanelSeed({
          coalInputGW: formatCapacityGwInput(coalCap),
          gasInputGW: formatCapacityGwInput(gasCap),
          oilInputGW: formatCapacityGwInput(oilCap),
          bioInputGW: formatCapacityGwInput(bioCap),
          geoInputGW: formatCapacityGwInput(geoCap),
          pumpedStorageInputGW: formatCapacityGwInput(pumpedCap),
          pumpedStorageDurationInput: String(pumpedDurationHours),
          storageInputGW: formatCapacityGwInput(storageCap),
          storageDurationInput: String(batteryDurationHours),
        });
        // Initialize "last applied" baselines from loaded DB values so
        // Dispatchable-only Generate does not accidentally trigger capacity APIs.
        setLastAppliedCapacityInputs({
          solar: formatCapacityGwInput(solarCap ?? 0),
          wind: formatCapacityGwInput(windCap ?? 0),
          windOff: formatCapacityGwInput(windOffCap ?? 0),
          nuclear: formatCapacityGwInput(nuclearCap ?? 0),
          hydro: formatCapacityGwInput(hydroCap ?? 0)
        });
        setLastAppliedDispatchableInputs({
          bio: formatCapacityGwInput(bioCap),
          coal: formatCapacityGwInput(coalCap),
          geo: formatCapacityGwInput(geoCap),
          gas: formatCapacityGwInput(gasCap),
          oil: formatCapacityGwInput(oilCap),
          storage: formatCapacityGwInput(storageCap),
          duration: String(batteryDurationHours),
          pumpedStorage: formatCapacityGwInput(pumpedCap),
          pumpedDuration: String(pumpedDurationHours)
        });
      }
  
      /* ------------ parallel calls ------------ */
  
      const year = formData.year;
      const tasks = [];
  
      if (overwrite && nuclearCap > 0) {
        tasks.push(
          generationProfileAPI
            .getNuclearHourly(countryIso, effective.nuclear)
            .then((r) => ({ type: "nuclear", data: r }))
        );
      }
  
      // Wind / Solar / Wind-Offshore hourly profiles depend on the SUPPLY
      // weather year, so refetch them whenever capacity > 0 regardless of the
      // `overwrite` flag. This ensures supply-year-only changes still update
      // the hourly profiles while user-entered capacity inputs are preserved
      // (via `effective.*`).
      if (windCap > 0) {
        tasks.push(
          fetchWindHourlyProfile(effective.wind)
            .then((r) => ({ type: "wind", data: r }))
        );
      }

      if (solarCap > 0) {
        tasks.push(
          fetchSolarHourlyProfile(effective.solar)
            .then((r) => ({ type: "solar", data: r }))
        );
      }

      if (windOffCap > 0) {
        tasks.push(
          fetchWindOffHourlyProfile(effective.windOff)
            .then((r) => ({ type: "windoff", data: r }))
        );
      }
  
      // Hydro monthly budgets are now weather-year-sensitive and yoked to the
      // same supplyWeatherYear that drives solar/wind CF lookups. The backend
      // walks a 3-tier fallback ladder per HYDRO_WEATHER_YEAR_METHODOLOGY.md.
      tasks.push(
        generationProfileAPI
          .getHydroMonthlyBudget(countryIso, hydroCap, supplyWeatherYearRef.current)
          .then((r) => ({ type: "hydroBudget", data: r }))
      );
  
      tasks.push(
        capacityAPI
          .getMustRunCF(countryIso, year || 2022)
          .then((r) => ({ type: "mustRunCF", data: r }))
      );
  
      const results = await Promise.allSettled(tasks);
  
      const parallelTaskLabels = [];
      if (overwrite && nuclearCap > 0) parallelTaskLabels.push("nuclear");
      if (windCap > 0) parallelTaskLabels.push("wind");
      if (solarCap > 0) parallelTaskLabels.push("solar");
      if (windOffCap > 0) parallelTaskLabels.push("windoff");
      parallelTaskLabels.push("hydroBudget");
      parallelTaskLabels.push("mustRunCF");

      const parallelErrors = [];
      results.forEach((r, index) => {
        const type = parallelTaskLabels[index];

        if (r.status !== "fulfilled") {
          const errorMessage = r.reason?.response?.data?.message || r.reason?.message || `Failed to load ${type}`;
          if (type === "wind") {
            setWindProfile(null);
            setWindSelectedCells(null);
            setWindLoading(false);
            parallelErrors.push(`Wind profile: ${errorMessage}`);
          } else if (type === "solar") {
            setSolarProfile(null);
            setSolarSelectedCells(null);
            setSolarLoading(false);
            parallelErrors.push(`Solar profile: ${errorMessage}`);
          } else if (type === "windoff") {
            setWindOffProfile(null);
            setWindOffSelectedCells(null);
            setWindOffLoading(false);
            parallelErrors.push(`Offshore wind profile: ${errorMessage}`);
          } else if (type === "nuclear") {
            setNuclearProfile(null);
            parallelErrors.push(`Nuclear profile: ${errorMessage}`);
          } else {
            parallelErrors.push(errorMessage);
          }
          return;
        }

        const { data } = r.value;

        if (type === "nuclear") {
          if (data?.profile) {
            setNuclearProfile(data.profile);
          } else {
            setNuclearProfile(null);
            parallelErrors.push("Nuclear profile endpoint returned no data");
          }
        }

        if (type === "wind") {
          if (data?.profile) {
            setWindProfile(data.profile);
            setWindSelectedCells(data.selected_cells || null);
            if (data.status_messages?.length) {
              data.status_messages.forEach((msg) => addLog(msg, 'backend'));
            }
          } else {
            setWindProfile(null);
            setWindSelectedCells(null);
            parallelErrors.push("Wind profile endpoint returned no data");
          }
          setWindLoading(false);
        }

        if (type === "solar") {
          if (data?.profile) {
            setSolarProfile(data.profile);
            setSolarSelectedCells(data.selected_cells || null);
            if (data.status_messages?.length) {
              data.status_messages.forEach((msg) => addLog(msg, 'backend'));
            }
          } else {
            setSolarProfile(null);
            setSolarSelectedCells(null);
            parallelErrors.push("Solar profile endpoint returned no data");
          }
          setSolarLoading(false);
        }

        if (type === "windoff") {
          if (data?.profile) {
            setWindOffProfile(data.profile);
            setWindOffSelectedCells(data.selected_cells || null);
            if (data.status_messages?.length) {
              data.status_messages.forEach((msg) => addLog(msg, 'backend'));
            }
          } else {
            setWindOffProfile(null);
            setWindOffSelectedCells(null);
            parallelErrors.push("Offshore wind profile endpoint returned no data");
          }
          setWindOffLoading(false);
        }

        if (type === "hydroBudget") {
          if (data?.monthly_generation_mwh) {
            setHydroMonthlyBudgetMWh(
              data.monthly_generation_mwh
            );
            // scaleFactor denominator: prefer the year-matched EMBER capacity
            // that the /hydro-budget endpoint now resolves (via
            // energy_metrics_consolidated.ember_capacity_gw with an IRENA
            // fallback). Keeps the reported utilization consistent with the
            // supply weather year. Falls back to the legacy irena_capacity_gw
            // echo if the new field is missing (older backend builds).
            setHydroIrenaCapacityGW(
              data.ember_capacity_gw ?? data.irena_capacity_gw ?? 0
            );
          }
        }

        if (type === "mustRunCF") {
          setMustRunCF(data?.fuels || null);
        }
      });

      if (parallelErrors.length > 0) {
        setError(parallelErrors[0]);
      }
  
    } catch (err) {
      console.error(err);

      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to load capacity data';
      setCapacityError(msg);
      addErrorLog(`Capacity fetch failed: ${msg}`);
      toast.error(msg.length > 160 ? `${msg.slice(0, 157)}…` : msg);

      // handleSubmit leaves isProcessing true until simulation runs; if capacity
      // never loads, profiles stay null and simulation never completes — clear it.
      setIsProcessing(false);

      setSolarProfile(null);
      setWindProfile(null);
      setWindOffProfile(null);
      setNuclearProfile(null);
      // Reset simulator map selections so stale cells from a previous
      // successful load are not left visible when capacity fetch fails.
      setSolarSelectedCells(null);
      setWindSelectedCells(null);
      setWindOffSelectedCells(null);
      setSolarLoading(false);
      setWindLoading(false);
      setWindOffLoading(false);
      setPumpedStorageCapacityGW(null);
      setPumpedStorageDurationH(DEFAULT_PUMPED_STORAGE_DURATION_H);
      applyInputsPanelSeed({
        pumpedStorageInputGW: '',
        pumpedStorageDurationInput: String(DEFAULT_PUMPED_STORAGE_DURATION_H),
      });
    } finally {
      setCapacityLoading(false);
    }
  }, [countryIso, formData.year, supplyWeatherYear, addLog, applyInputsPanelSeed, fetchSolarHourlyProfile, fetchWindHourlyProfile, fetchWindOffHourlyProfile]);

  // Fetch capacity data when component mounts or country / model year /
  // supply weather year changes. (Demand weather year is handled by a
  // separate effect that re-runs /generation-profile.)
  useEffect(() => {
    if (!countryIso || !formData.year || !supplyWeatherYear) return;
    if (parsedVedaLookup && !parsedShareScenario && !vedaOverlayReady) return;
    const prev = prevCapacityDepsRef.current;
    const onlySupplyYearChanged =
      prev.countryIso === countryIso &&
      prev.year === formData.year &&
      prev.supplyWeatherYear !== supplyWeatherYear;
    prevCapacityDepsRef.current = { countryIso, year: formData.year, supplyWeatherYear };
    fetchCapacityData({
      overwriteInputs: !onlySupplyYearChanged
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchCapacityData,
    countryIso,
    formData.year,
    supplyWeatherYear,
    vedaOverlayReady,
    parsedVedaLookup,
    parsedShareScenario,
  ]);

  // Handle submit for generation profile - with useDefault flag to control demand behavior
  const handleSubmit = useCallback(async (useDefault = false) => {
    if (!countryIso) {
      toast.error('Country ISO code is required');
      return;
    }
    
    if (formData.year < 2000 || formData.year > 2022) {
      toast.error('Year must be between 2000 and 2022');
      return;
    }

    addSeparator();
    prevSimulationLogSigRef.current = null;
    const submitGen = ++demandProfileSubmitGenRef.current;
    setIsProcessing(true);
    setHasError(false);
    setLoading(true);
    setError(null);
    // Keep existing chartData during same-country refresh so Highcharts instance
    // stays mounted and user-applied filters (legend visibility, zoom window)
    // can persist across data updates.
    addLog('Initializing generation profile calculation...');

    try {
      // If useDefault is true (country change), force null to fetch EMBER default
      // Otherwise, use demand input ref if available
      const demandValue = useDefault 
        ? null 
        : (demandTwhRef.current && !inputsLiveRef.current.demandTwhError
          ? parseFloat(demandTwhRef.current)
          : null);
      
      const requestData = {
        isoCode: countryIso,
        year: parseInt(formData.year),
        totalGenerationTwh: demandValue,
        weatherYear: weatherYearRef.current
      };

      addLog('Fetching demand profile and capacity data...');
      const response = await api.post('/generation-profile', requestData);

      if (submitGen !== demandProfileSubmitGenRef.current) {
        return;
      }
      
      if (response.data.success) {
        setChartData(response.data.data);
        // Add backend status messages if present
        if (response.data.status_messages && Array.isArray(response.data.status_messages)) {
          response.data.status_messages.forEach(msg => addLog(msg, 'backend'));
        }
        addLog('Demand profile and capacity data loaded successfully');
        toast.success(`Profile generated successfully for ${countryIso} in ${formData.year}!`);
        // Don't set isProcessing to false here - wait for simulation to complete
      } else {
        throw new Error(response.data.message || 'Failed to generate profile');
      }
    } catch (error) {
      if (submitGen !== demandProfileSubmitGenRef.current) {
        return;
      }
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      setError(errorMessage);
      addErrorLog(`Error: ${errorMessage}`);
      setIsProcessing(false);
      toast.error(errorMessage);
    } finally {
      if (submitGen === demandProfileSubmitGenRef.current) {
        setLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryIso, formData.year, addLog, addErrorLog, addSeparator]);

  // Re-fetch demand profile once VO report demand is applied (initial handleSubmit may run first).
  useEffect(() => {
    if (!parsedVedaLookup || parsedShareScenario || !vedaOverlayReady) return;
    if (!skipDemandAutofillFromChartDataRef.current || !demandTwhRef.current) return;
    if (vedaReportProfileFetchedRef.current) return;
    if (!countryIso || !formData.year) return;

    vedaReportProfileFetchedRef.current = true;
    handleSubmit(false);
  }, [
    parsedVedaLookup,
    parsedShareScenario,
    vedaOverlayReady,
    countryIso,
    formData.year,
    handleSubmit,
  ]);

  // Auto-generate timeline chart when DEMAND weather year changes
  // (preserve user's demand input). Supply weather year changes are handled
  // by a separate fetchCapacityData effect above.
  useEffect(() => {
    // Skip if initial load or loading in progress
    if (!countryIso || !formData.year || !weatherYear || loading) return;
    
    // Skip on initial mount (country effect will handle initial load)
    if (prevCountryRef.current === null) return;
    
    // Only trigger if we're on the same country (weather year change, not country change)
    if (prevCountryRef.current === countryIso) {
      handleSubmit(false); // Preserve user's demand input
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weatherYear]);

  // Log simulation pipeline steps once per stable result (not on every profile array replacement).
  useEffect(() => {
    if (!simulation) return;
    if (loading || capacityLoading || solarLoading || windLoading || windOffLoading) return;
    if (!chartData?.hourlyProfile) return;

    const hydroSig =
      (hydroCapacityGW || 0) === 0
        ? 'na'
        : hydroMonthlyBudgetMWh?.length === 12
          ? String(hydroMonthlyBudgetMWh[0])
          : capacityLoading || solarLoading || windLoading || windOffLoading
            ? 'pending'
            : 'missing';
    if (hydroSig === 'pending') return;

    const sig = [
      countryIso,
      formData.year,
      supplyWeatherYear,
      weatherYear,
      solarProfile?.length ?? 0,
      windProfile?.length ?? 0,
      nuclearProfile?.length ?? 0,
      mustRunCF?.coal?.utilization_factor ?? '',
      mustRunCF?.bioenergy?.utilization_factor ?? '',
      hydroSig,
      gasCapacityGW ?? 0,
      coalCapacityGW ?? 0,
      bioCapacityGW ?? 0,
      oilCapacityGW ?? 0,
      storageCapacityGW ?? 0,
      pumpedStorageCapacityGW ?? 0,
      simulation?.dispatchMode ?? '',
      simulation?.meritOrder?.join('>') ?? '',
    ].join('|');
    if (prevSimulationLogSigRef.current === sig) return;
    prevSimulationLogSigRef.current = sig;

    const messages = [
      'Calculating residual demand after must-run baseload and renewables...',
    ];
    if ((storageCapacityGW || 0) > 0 || (pumpedStorageCapacityGW || 0) > 0) {
      messages.push('Calculating storage operation (charge/discharge) from residual demand...');
    }
    if (hydroMonthlyBudgetMWh) {
      messages.push('Calculating hydro dispatch from residual demand using monthly generation budget...');
    }
    const flexMeritLabel = simulation?.dispatchMode === 'vo-report-tech' && simulation?.meritOrder?.length
      ? (simulation.meritOrder.length <= 6
          ? simulation.meritOrder.join(' → ')
          : `${simulation.meritOrder.slice(0, 4).join(' → ')} → … (${simulation.meritOrder.length} techs)`)
      : 'bio → coal → gas → oil (legacy)';
    if ((gasCapacityGW || 0) > 0 || (coalCapacityGW || 0) > 0 || (bioCapacityGW || 0) > 0 || (oilCapacityGW || 0) > 0) {
      messages.push(`Calculating flex fossil dispatch (${flexMeritLabel})...`);
    }
    messages.push(
      'Calculating fuel utilization (capacity factors) for all fuels...',
      'Calculating system balance metrics (shortage, surplus, curtailment)...',
      'Generating final stacked generation profile...',
      'Processing complete! ✓'
    );

    addLogs(messages);
    showLatestLog();
    setIsProcessing(false);
  }, [
    simulation,
    loading,
    capacityLoading,
    solarLoading,
    windLoading,
    windOffLoading,
    chartData,
    countryIso,
    formData.year,
    supplyWeatherYear,
    weatherYear,
    solarProfile,
    windProfile,
    nuclearProfile,
    mustRunCF,
    hydroMonthlyBudgetMWh,
    hydroCapacityGW,
    gasCapacityGW,
    coalCapacityGW,
    bioCapacityGW,
    oilCapacityGW,
    storageCapacityGW,
    pumpedStorageCapacityGW,
    addLogs,
    showLatestLog,
    setIsProcessing,
  ]);

  // Fetch available weather years when country changes, then trigger generation profile
  useEffect(() => {
    const fetchWeatherYears = async () => {
      if (!countryIso) {
        setAvailableWeatherYears([]);
        weatherYearRef.current = 2013;
        setWeatherYear(2013);
        return;
      }

      setWeatherYearsLoading(true);
      setWeatherYearsError(null);

      try {
        const response = await generationProfileAPI.getWeatherYears(countryIso);
        if (response?.success && Array.isArray(response.weather_years)) {
          const years = response.weather_years;
          setAvailableWeatherYears(years);
          
          // Preserve current weatherYear if available in new country's list, else fallback
          const currentYear = weatherYearRef.current;
          let resolvedYear;
          if (years.includes(currentYear)) {
            resolvedYear = currentYear;
          } else if (years.includes(2013)) {
            resolvedYear = 2013;
          } else if (years.length > 0) {
            resolvedYear = years[0];
          } else {
            resolvedYear = 2013;
          }
          weatherYearRef.current = resolvedYear;
          setWeatherYear(resolvedYear);

          // Trigger generation profile with the resolved (correct) weather year.
          // VO ?veda= with report demand: wait for capacity-mix — do not fetch EMBER default first.
          if (formData.year) {
            const vedaLookup = parsedVedaLookupRef.current;
            if (vedaLookupExpectsReportDemand(vedaLookup)) {
              // handleSubmit runs after VO report demand is applied (see effect below).
            } else {
              const useDefaultDemand = !scenarioUrlHasExplicitDemandTwh(parsedShareScenarioRef.current)
                && !vedaLookupHasExplicitDemandTwh(vedaLookup);
              setTimeout(() => { handleSubmit(useDefaultDemand); }, 0);
            }
          }
        } else {
          setWeatherYearsError('No weather years available');
          setAvailableWeatherYears([]);
          weatherYearRef.current = 2013;
          setWeatherYear(2013);
        }
      } catch (e) {
        console.error('❌ Error fetching weather years:', e);
        setWeatherYearsError(e?.response?.data?.message || e.message || 'Failed to fetch weather years');
        setAvailableWeatherYears([]);
        weatherYearRef.current = 2013;
        setWeatherYear(2013);
      } finally {
        setWeatherYearsLoading(false);
      }
    };

    fetchWeatherYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryIso]);

  // Reset charts and show waiting indicators when country or year changes (not when only weather year changes)
  useEffect(() => {
    if (!countryIso) return;
    const isInitialLoad = prevCountryRef.current === null;
    const isCountryChanged = !isInitialLoad && prevCountryRef.current !== countryIso;
    const isYearChanged = !isInitialLoad && prevYearRef.current !== formData.year;
    const shouldFullReset = isInitialLoad || isCountryChanged || isYearChanged;
    if (shouldFullReset) {
      prevCountryRef.current = countryIso;
      prevYearRef.current = formData.year;
      prevSimulationLogSigRef.current = null;
      // Country/model-year reset should also clear saved interaction state
      // so a new country's chart starts from default view.
      hourlyChartStateRef.current = { zoom: null, legendVisibility: {} };
      setVoFlexFuelCollapsed({});

      setChartData(null);
      setError(null);
      setCapacityError(null);
      setCapacityLoading(true);
      setSolarProfile(null);
      setSolarLoading(true);
      // Wind
      setWindProfile(null);
      setWindLoading(true);
      // Nuclear
      setNuclearProfile(null);
      setNuclearCapacityGW(null);
      // Hydro
      setHydroProfile(null);
      setHydroCapacityGW(null);
      setHydroMonthlyBudgetMWh(null);
      setHydroIrenaCapacityGW(null);
      hydroFetchRef.current = { countryIso: null, year: null };
      setPumpedStorageCapacityGW(null);
      setPumpedStorageDurationH(DEFAULT_PUMPED_STORAGE_DURATION_H);
      // Reset dispatchable & storage capacities
      setCoalCapacityGW(null);
      setGasCapacityGW(null);
      setOilCapacityGW(null);
      setBioCapacityGW(null);
      setGeoCapacityGW(null);
      setStorageCapacityGW(null);
      setStorageDurationH(DEFAULT_STORAGE_DURATION_H);
      setMustRunCF(null);
      resetInputsPanel();
      setLastAppliedDispatchableInputs({
        bio: '',
        coal: '',
        geo: '',
        gas: '',
        oil: '',
        storage: '',
        duration: String(DEFAULT_STORAGE_DURATION_H),
        pumpedStorage: '',
        pumpedDuration: String(DEFAULT_PUMPED_STORAGE_DURATION_H)
      });
      setLastAppliedVoSubtech({});
      setVoSubtechCapacityApplied(null);
    }
    // Note: handleSubmit is triggered from the weather years fetch effect after country change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryIso, formData.year]);

  // Populate Demand (TWh) input when chartData arrives
  // Note: Clearing is handled by country change useEffect, not here
  useEffect(() => {
    if (!chartData || typeof chartData.totalGenerationTwh !== 'number') return;
    if (skipDemandAutofillFromChartDataRef.current) return;
    const value = chartData.totalGenerationTwh.toString();
    applyInputsPanelSeed({ demandTwhInput: value, demandTwhError: '' });
    setLastAppliedDemandTwhInput(value);
    demandTwhRef.current = value;
  }, [chartData, applyInputsPanelSeed]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle Weather Year change - memoized for performance
  const handleWeatherYearChange = useCallback((e) => {
    const newYear = Number(e.target.value);
    if (!isNaN(newYear) && newYear > 0) {
      weatherYearRef.current = newYear;
      setWeatherYear(newYear);
    }
  }, []);

  // Handle Supply Weather Year change — drives Solar/Wind/WindOff hourly APIs
  const handleSupplyWeatherYearChange = useCallback((e) => {
    const newYear = Number(e.target.value);
    if (!isNaN(newYear) && newYear > 0) {
      supplyWeatherYearRef.current = newYear;
      setSupplyWeatherYear(newYear);
    }
  }, []);

  // Handle Demand Go button click - use custom demand value
  const handleDemandGo = async () => {
    const live = inputsLiveRef.current;
    if (!demandTwhRef.current || live.demandTwhError) return;

    const numValue = parseFloat(demandTwhRef.current);
    if (isNaN(numValue) || numValue <= 0) {
      applyInputsPanelSeed({
        demandTwhError: 'Please enter a valid number greater than 0',
      });
      return;
    }
    if (numValue > 99999) {
      applyInputsPanelSeed({
        demandTwhError: 'Value must be between 0.1 and 99,999 TWh',
      });
      return;
    }
    
    if (!countryIso) {
      setError('Please select a country first');
      return;
    }
    
    setIsDemandGoLoading(true);
    
    try {
      // Use handleSubmit with useDefault=false to preserve user's demand input
      await handleSubmit(false);
      setLastAppliedDemandTwhInput(demandTwhRef.current);
      toast.success(`Profile generated successfully with ${numValue} TWh demand!`);
    } catch (err) {
      console.error('Error from handleDemandGo:', err);
    } finally {
      setIsDemandGoLoading(false);
    }
  };

  // Unified Generate: run existing section handlers in order
  const scrollToHourlyChartSection = useCallback(() => {
    requestAnimationFrame(() => {
      const element = hourlyChartSectionRef.current;
      if (!element) return;
      const yOffset = -24;
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    });
  }, []);

  const handleUnifiedGenerate = useCallback(async () => {
    if (unifiedGenerateInFlightRef.current) return;
    const runState = canRunUnifiedGenerate({
      live: inputsLiveRef.current,
      lastAppliedDemandTwhInput,
      lastAppliedCapacityInputs,
      lastAppliedDispatchableInputs,
      lastAppliedVoSubtech,
      capacityLimits: renewableCapacityLimits,
      chartData,
      nuclearProfile,
      solarProfile,
      windProfile,
    });
    if (runState.capacityLimitsExceeded) {
      const firstError = Object.values(runState.capacityInputErrors)[0];
      toast.error(firstError || 'One or more inputs exceed the country capacity limit.');
      return;
    }
    if (!runState.anyRunnable) return;
    const { canRunDemand, canRunCapacity, canRunDispatchable } = runState;

    unifiedGenerateInFlightRef.current = true;
    scrollToHourlyChartSection();
    try {
      await waitForNextPaint();
      if (canRunDemand) await handleDemandGo();
      if (canRunCapacity) await handleCapacitySubmit();
      if (canRunDispatchable) await handleDispatchableStorageSubmit();
    } finally {
      unifiedGenerateInFlightRef.current = false;
      if (isCompactViewportRef.current) {
        mobilePanelsRef.current?.closePanels();
        desktopInputsSidebarRef.current?.collapse();
        desktopRightPanelRef.current?.collapse();
        scheduleDeferredChartReflowRef.current?.();
        setTimeout(scrollToHourlyChartSection, 150);
      }
    }
  }, [
    lastAppliedDemandTwhInput,
    lastAppliedCapacityInputs,
    lastAppliedDispatchableInputs,
    lastAppliedVoSubtech,
    renewableCapacityLimits,
    chartData,
    nuclearProfile,
    solarProfile,
    windProfile,
    handleDemandGo,
    handleCapacitySubmit,
    handleDispatchableStorageSubmit,
    scrollToHourlyChartSection,
  ]);

  const handleCopySimulatorScenarioLink = useCallback(async () => {
    if (!countryIso) {
      toast.error('Country is required to build a link');
      return;
    }
    const live = inputsLiveRef.current;
    const capacityInputErrors = getCapacityInputErrors(live, renewableCapacityLimits);
    if (Object.keys(capacityInputErrors).length > 0) {
      toast.error('Fix capacity limit errors before copying a link.');
      return;
    }

    const path =
      typeof window !== 'undefined' ? window.location.pathname : `/country/${countryIso}/simulator`;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';

    // VO Type 2: share lookup (scen|scenario), not Type 1 mix JSON — otherwise reload
    // drops report costs / VO VRE / flex catalog dispatch.
    if (parsedVedaLookup) {
      try {
        const demandN = parseFloat(String(live.demandTwhInput ?? '').trim());
        const lookupForShare = {
          ...parsedVedaLookup,
          weatherYear,
          supplyWeatherYear,
          ...(Number.isFinite(demandN) && demandN > 0 ? { demandTwh: demandN } : {}),
        };
        const qs = buildVedaLookupQueryString(lookupForShare);
        const url = `${origin}${path}?${qs}`;
        await navigator.clipboard.writeText(url);
        toast.success('VO simulator link copied to clipboard');
      } catch (e) {
        console.error(e);
        toast.error(e?.message || 'Could not build VO share link');
      }
      return;
    }

    const parseDuration = (value, fallback) => {
      const n = parseFloat(String(value ?? '').trim());
      return Number.isFinite(n) && n > 0 ? n : fallback;
    };
    const payload = buildSimulatorScenarioV1({
      weatherYear,
      supplyWeatherYear,
      demandTwhInput: live.demandTwhInput,
      solarInputGW: live.solarInputGW,
      windInputGW: live.windInputGW,
      windOffInputGW: live.windOffInputGW,
      nuclearInputGW: live.nuclearInputGW,
      hydroInputGW: live.hydroInputGW,
      coalInputGW: live.coalInputGW,
      gasInputGW: live.gasInputGW,
      oilInputGW: live.oilInputGW,
      bioInputGW: live.bioInputGW,
      geoInputGW: live.geoInputGW,
      storageInputGW: live.storageInputGW,
      storageDurationH: parseDuration(live.storageDurationInput, storageDurationH),
      pumpedStorageInputGW: live.pumpedStorageInputGW,
      pumpedStorageDurationH: parseDuration(live.pumpedStorageDurationInput, pumpedStorageDurationH),
    });
    const enc = encodeSimulatorScenarioSearchParam(payload);
    const url = `${origin}${path}?${SIMULATOR_SCENARIO_QUERY_KEY}=${enc}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Simulator link copied to clipboard');
    } catch (e) {
      console.error(e);
      toast.error('Could not copy link (clipboard permission denied?)');
    }
  }, [
    countryIso,
    renewableCapacityLimits,
    weatherYear,
    supplyWeatherYear,
    storageDurationH,
    pumpedStorageDurationH,
    parsedVedaLookup,
  ]);

  // Expose method for parent (e.g. info icon click) to open first accordion
  React.useImperativeHandle(ref, () => ({
    openFirstAccordion: () => methodologyAccordionRef.current?.openFirstAccordion?.(),
  }), []);

  // Reset no longer needed; chart auto-loads on ISO/year change

  const captureHourlyLegendVisibility = useCallback((chart) => {
    if (!chart?.series) return;
    const next = {};
    chart.series.forEach((s) => {
      const key = s?.options?.id || s?.name;
      if (!key) return;
      next[key] = s.visible !== false;
    });
    hourlyChartStateRef.current.legendVisibility = next;
  }, []);

  const restoreHourlyChartInteractionState = useCallback((chart) => {
    if (!chart || isRestoringHourlyChartStateRef.current) return;
    const xAxis = chart.xAxis?.[0];
    if (!xAxis) return;

    const { zoom, legendVisibility } = hourlyChartStateRef.current;
    let shouldRedraw = false;

    isRestoringHourlyChartStateRef.current = true;
    try {
      chart.series?.forEach((s) => {
        const key = s?.options?.id || s?.name;
        if (!key || !Object.prototype.hasOwnProperty.call(legendVisibility, key)) return;
        const expectedVisible = legendVisibility[key];
        if (s.visible !== expectedVisible) {
          s.setVisible(expectedVisible, false);
          shouldRedraw = true;
        }
      });

      if (zoom && Number.isFinite(zoom.min) && Number.isFinite(zoom.max)) {
        const dataMin = Number.isFinite(xAxis.dataMin) ? xAxis.dataMin : zoom.min;
        const dataMax = Number.isFinite(xAxis.dataMax) ? xAxis.dataMax : zoom.max;
        const clampedMin = Math.max(dataMin, zoom.min);
        const clampedMax = Math.min(dataMax, zoom.max);
        if (Number.isFinite(clampedMin) && Number.isFinite(clampedMax) && clampedMin < clampedMax) {
          const currentMin = xAxis.min;
          const currentMax = xAxis.max;
          if (currentMin !== clampedMin || currentMax !== clampedMax) {
            xAxis.setExtremes(clampedMin, clampedMax, false, false, { trigger: 'restore' });
            shouldRedraw = true;
          }
        }
      }

      if (shouldRedraw) chart.redraw();
    } finally {
      isRestoringHourlyChartStateRef.current = false;
    }
  }, []);
  restoreHourlyChartInteractionStateRef.current = restoreHourlyChartInteractionState;

  /** Reflow chart layout (HTML legend is outside the plot; compact bottom margin hugs navigator). */
  const kickHourlyStockChartLayout = useCallback((chart) => {
    if (!chart || chart.destroyed) return;
    if (isCompactViewport || hourlyMobileFullscreenOpen) {
      syncCompactChartBottomMargin(chart);
    }
    if (typeof chart.reflow === 'function') chart.reflow();
    if (typeof chart.redraw === 'function') chart.redraw(false);
  }, [hourlyMobileFullscreenOpen, isCompactViewport]);

  const resetHourlyChartLayoutReady = useCallback((reason = 'unspecified') => {
    hourlyChartInitTokenRef.current += 1;
    hourlyLayoutRevealedSignatureRef.current = null;
    setHourlyChartLayoutReady(false);
  }, []);

  const handleHourlyFullscreenLegendHeight = useCallback((height) => {
    setHourlyFullscreenLegendHeight((prev) => (Math.abs(prev - height) < 1 ? prev : height));
  }, []);

  const reportHourlyFullscreenChartHostHeight = useCallback(() => {
    const host = hourlyFullscreenChartHostRef.current;
    if (!host) return;
    const measured = Math.floor(host.getBoundingClientRect().height);
    if (measured <= 0) return;
    setHourlyFullscreenPlotHeight((prev) =>
      Math.abs(prev - measured) < 2 ? prev : measured
    );
  }, []);

  const handleHourlyFullscreenBodySizeChange = useCallback(
    ({ bodyHeight }) => {
      hourlyFullscreenBodyHeightRef.current = bodyHeight;
      setHourlyFullscreenPlotHeight((prev) => {
        const next = computeFullscreenStockChartHeight(bodyHeight, hourlyFullscreenLegendHeight);
        return Math.abs(prev - next) < 2 ? prev : next;
      });
      requestAnimationFrame(reportHourlyFullscreenChartHostHeight);
    },
    [hourlyFullscreenLegendHeight, reportHourlyFullscreenChartHostHeight]
  );

  /** Mobile only: portrait-friendly full-screen modal (no screen.orientation.lock). */
  const handleHourlyChartFullscreen = useCallback(() => {
    const chart = chartRef.current?.chart;
    if (chart && !chart.destroyed) {
      captureHourlyLegendVisibility(chart);
    }
    setHourlyMobileFullscreenOpen(true);
    resetHourlyChartLayoutReady('open_fullscreen');
  }, [captureHourlyLegendVisibility, resetHourlyChartLayoutReady]);

  const handleCloseHourlyMobileFullscreen = useCallback(() => {
    const chart = chartRef.current?.chart;
    if (chart && !chart.destroyed) {
      captureHourlyLegendVisibility(chart);
    }
    setHourlyMobileFullscreenOpen(false);
    resetHourlyChartLayoutReady('close_fullscreen');
  }, [captureHourlyLegendVisibility, resetHourlyChartLayoutReady]);

  /**
   * Reveal hourly chart (same UX: skeleton/invisible until hourlyChartLayoutReady).
   * Always scheduled outside React commit (setTimeout) so we never flushSync
   * from an effect. setHourlyChartLayoutReady runs before chart.redraw().
   */
  const scheduleRevealHourlyChartLayout = useCallback(
    (chart, reason) => {
      if (!chart || chart.destroyed) return;
      const seriesCount = chart.series?.length ?? 0;
      const signature = `${seriesCount}|${isCompactViewport}|${hourlyMobileFullscreenOpen}|${hourlyVisibleMonthIndex}`;
      if (hourlyLayoutRevealedSignatureRef.current === signature) {
        return;
      }

      if (hourlyRevealTimeoutRef.current != null) {
        clearTimeout(hourlyRevealTimeoutRef.current);
      }

      hourlyRevealTimeoutRef.current = window.setTimeout(() => {
        hourlyRevealTimeoutRef.current = null;
        if (chart.destroyed) return;
        if (hourlyLayoutRevealedSignatureRef.current === signature) return;

        hourlyLayoutRevealedSignatureRef.current = signature;

        setHourlyChartLayoutReady(true);

        const token = hourlyChartInitTokenRef.current;
        requestAnimationFrame(() => {
          if (token !== hourlyChartInitTokenRef.current || chart.destroyed) return;
          if (isCompactViewport || hourlyMobileFullscreenOpen) {
            syncCompactChartBottomMargin(chart);
          }
          if (typeof chart.reflow === 'function') {
            chart.reflow();
          }
        });

        requestAnimationFrame(() => {
          if (token !== hourlyChartInitTokenRef.current || chart.destroyed) return;
          requestAnimationFrame(() => {
            if (token !== hourlyChartInitTokenRef.current || chart.destroyed) return;
            if (typeof chart.redraw === 'function') {
              chart.redraw(false);
            }
          });
        });
      }, 0);
    },
    [hourlyMobileFullscreenOpen, hourlyVisibleMonthIndex, isCompactViewport]
  );
  scheduleRevealHourlyChartLayoutRef.current = scheduleRevealHourlyChartLayout;

  const hourlyChartYear = formData.year || chartData?.year || 2022;

  const clearHourlyChartZoomState = useCallback(() => {
    hourlyChartStateRef.current.zoom = null;
  }, []);

  /** Compact monthly view: show the full selected month (clears shortage week zoom). */
  const fitHourlyChartToVisibleMonth = useCallback(
    (chart, monthIndex = hourlyVisibleMonthIndex) => {
      if (!chart || chart.destroyed || !isCompactViewportRef.current) return;
      const xAxis = chart.xAxis?.[0];
      if (!xAxis) return;
      const year = formData?.year || chartData?.year || 2022;
      const { startMs, endMsExclusive } = getMonthHourBounds(year, monthIndex);
      isRestoringHourlyChartStateRef.current = true;
      try {
        hourlyChartStateRef.current.zoom = null;
        xAxis.setExtremes(startMs, endMsExclusive - 1, true, false, { trigger: 'monthNav' });
      } finally {
        isRestoringHourlyChartStateRef.current = false;
      }
    },
    [chartData?.year, formData?.year, hourlyVisibleMonthIndex]
  );

  const scheduleCompactMonthViewportFit = useCallback(
    (monthIndex) => {
      if (!isCompactViewportRef.current) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const chart = chartRef.current?.chart;
          if (chart && !chart.destroyed) {
            fitHourlyChartToVisibleMonth(chart, monthIndex);
          }
        });
      });
    },
    [fitHourlyChartToVisibleMonth]
  );

  const handleHourlyVisibleMonthPrevious = useCallback(() => {
    skipNextCompactMonthFitRef.current = false;
    clearHourlyChartZoomState();
    setHourlyVisibleMonthIndex((m) => Math.max(0, m - 1));
  }, [clearHourlyChartZoomState]);

  const handleHourlyVisibleMonthNext = useCallback(() => {
    skipNextCompactMonthFitRef.current = false;
    clearHourlyChartZoomState();
    setHourlyVisibleMonthIndex((m) => Math.min(11, m + 1));
  }, [clearHourlyChartZoomState]);

  // Series data — recomputed on month slice / simulation / flex-fuel changes only.
  const hasHourlyChartData = Boolean(chartData?.hourlyProfile && simulation);
  const compactMonthKey = isCompactViewport ? hourlyVisibleMonthIndex : -1;
  const hourlyChartSeries = useMemo(() => {
    if (!hasHourlyChartData) return null;
    const year = formData.year || chartData.year || 2022;
    return buildHourlyChartSeries({
      hourlyProfile: chartData.hourlyProfile,
      simulation,
      solarProfile,
      windProfile,
      windOffProfile,
      nuclearProfile,
      hydroProfile,
      year,
      visibleMonthIndex: compactMonthKey >= 0 ? compactMonthKey : 0,
      useMonthlyWindow: isCompactViewport,
      voFlexFuelCollapsed,
      legendVisibility: hourlyChartStateRef.current.legendVisibility,
    });
  }, [
    hasHourlyChartData,
    chartData?.hourlyProfile,
    chartData?.year,
    simulation,
    solarProfile,
    windProfile,
    windOffProfile,
    nuclearProfile,
    hydroProfile,
    formData.year,
    compactMonthKey,
    isCompactViewport,
    voFlexFuelCollapsed,
  ]);

  // Options shell — layout, axes, tooltip, events (stable across compact month nav).
  const hourlyChartOptionsShell = useMemo(() => {
    if (!hasHourlyChartData) return null;
    const hourlyMobileFullscreen = isMobileViewport && hourlyMobileFullscreenOpen;
    const compact = isCompactViewport || hourlyMobileFullscreen;
    const mobileInline = isMobileViewport && !hourlyMobileFullscreen;
    // Mobile/tablet: hide top range-selector buttons; keep bottom navigator + scrollbar.
    const compactChartHeight = hourlyMobileFullscreen
      ? hourlyFullscreenPlotHeight
      : getHourlyCompactChartHeight(isMobileViewport);

    return {
      chart: {
        type: 'line',
        reflow: true,
        backgroundColor: 'transparent',
        style: { fontFamily: 'Inter, system-ui, sans-serif' },
        zoomType: 'xy',
        panning: { enabled: true, type: 'xy' },
        panKey: 'shift',
        // Compact: tight bottom margin (synced to navigator on load/redraw); desktop: fixed 420px plot
        marginBottom: compact
          ? Math.ceil(
              HOURLY_COMPACT_NAVIGATOR_HEIGHT +
                HOURLY_COMPACT_NAVIGATOR_MARGIN +
                HOURLY_COMPACT_SCROLLBAR_HEIGHT +
                2
            )
          : 25,
        marginTop: hourlyMobileFullscreen ? 28 : compact ? 24 : HOURLY_DESKTOP_CHART_MARGIN_TOP,
        spacingBottom: compact ? 0 : -5,
        spacingTop: compact ? 0 : -4,
        height: compact ? compactChartHeight : HOURLY_DESKTOP_PLOT_HEIGHT,
        events: {
          load: function() {
            const chart = this;
            const container = chart.container;
            if (container) {
              container.style.overflow = 'visible';
              if (container.parentNode) {
                container.parentNode.style.overflow = 'visible';
              }
              const clearHover = () => pushLeftPanelTooltipData(null);
              container.addEventListener('mouseleave', clearHover);
              chart._clearLeftPanelTooltip = clearHover;
            }
            attachLegendRightClickFocus(this);
            // Seed only once on fresh chart load; do not overwrite an existing
            // saved legend state that should persist across same-country refreshes.
            if (Object.keys(hourlyChartStateRef.current.legendVisibility || {}).length === 0) {
              captureHourlyLegendVisibility(this);
            }
            if (hourlyDataReadyRef.current) {
              restoreHourlyChartInteractionStateRef.current?.(chart);
              scheduleRevealHourlyChartLayoutRef.current?.(chart, 'highcharts_load');
            }
          },
          redraw: function() {
            attachLegendRightClickFocus(this);
          },
          fullscreenOpen: function() {
            const chart = this;
            const el = chart.renderTo || chart.container;
            if (el) {
              el.style.width = '100vw';
              el.style.height = '100vh';
              el.style.background = '#FFFFFF';
            }
            chart.update({ chart: { backgroundColor: '#FFFFFF' } }, false);
            setTimeout(function() {
              if (chart.reflow) chart.reflow();
            }, 0);
          },
          fullscreenClose: function() {
            const chart = this;
            const el = chart.renderTo || chart.container;
            if (el) {
              el.style.width = '';
              el.style.height = '';
              el.style.background = '';
            }
            chart.update({ chart: { backgroundColor: 'transparent' } }, false);
            setTimeout(function() {
              if (chart.reflow) chart.reflow();
            }, 0);
          },
          mouseOut: function() {
            pushLeftPanelTooltipData(null);
          },
          destroy: function() {
            const container = this.container;
            if (container && this._clearLeftPanelTooltip) {
              container.removeEventListener('mouseleave', this._clearLeftPanelTooltip);
              this._clearLeftPanelTooltip = null;
            }
          }
        }
      },
      navigator: {
        enabled: true,
        height: compact ? HOURLY_COMPACT_NAVIGATOR_HEIGHT : 40,
        margin: compact ? HOURLY_COMPACT_NAVIGATOR_MARGIN : 8,
        outlineColor: '#CBD5E1',
        maskFill: 'rgba(99, 102, 241, 0.1)',
        series: {
          color: '#A5B4FC',
          lineWidth: 1
        }
      },
      scrollbar: {
        enabled: true,
        height: compact ? HOURLY_COMPACT_SCROLLBAR_HEIGHT : 14,
        barBackgroundColor: '#CBD5E1',
        barBorderRadius: 4,
        buttonBackgroundColor: '#E2E8F0',
        trackBackgroundColor: '#F1F5F9'
      },
      title: { text: '' },
      xAxis: {
        type: 'datetime',
        title: { text: '' },
        labels: { 
          style: { color: '#64748B' },
          rotation: 0,
          align: 'center',
          formatter: function() {
            // Format the full date for tooltip
            const fullDate = Highcharts.dateFormat('%B %d, %Y %H:%M', this.value);
            // Format the display label (remove year if present)
            let displayLabel = Highcharts.dateFormat(this.dateTimeLabelFormat, this.value);
            // Remove year from display (e.g., "Jan 2022" becomes "Jan")
            displayLabel = displayLabel.replace(/\s*\d{4}$/, '');
            
            // Return HTML with title attribute for tooltip
            return `<span title="${fullDate}" style="cursor: help;">${displayLabel}</span>`;
          },
          useHTML: true
        },
        gridLineColor: '#E2E8F0',
        dateTimeLabelFormats: {
          day: '%b %d',
          week: '%b %d', 
          month: '%b',
          year: '%Y'
        },
        events: {
          afterSetExtremes: function(e) {
            if (isRestoringHourlyChartStateRef.current || e?.trigger === 'monthNav') return;
            if (Number.isFinite(e.min) && Number.isFinite(e.max)) {
              hourlyChartStateRef.current.zoom = { min: e.min, max: e.max };
            }
          }
        }
      },
      yAxis: {
        title: { text: 'GW' },
        labels: { style: { color: '#64748B' } },
        gridLineColor: '#E2E8F0',
        opposite: false,
        endOnTick: false,       // Don't force rounding to nearest tick
        showLastLabel: true,    // Display the actual peak value
        maxPadding: 0.02,       // Small 2% padding above peak
        alignTicks: false       // Allow flexible tick positioning
      },
      rangeSelector: {
        enabled: !compact,
        allButtonsEnabled: true,
        // Hide date inputs on phone inline view; show in mobile full-screen + tablet/desktop
        inputEnabled: !compact && !mobileInline,
        selected: 6, // Default to "All" option (index 6, after adding 1D)
        y: hourlyMobileFullscreen ? -12 : compact ? -18 : 0,
        buttonPosition: {
          align: 'left',
          x: compact ? 6 : 10
        },
        inputPosition: {
          align: 'right',
          x: compact ? -28 : -45
        },
        inputStyle: {
          color: '#6366F1', // Blue color to match the previous styling
          fontSize: '12px',
          fontWeight: '500'
        },
        inputBoxBorderColor: 'transparent',
        inputBoxWidth: 90,
        inputBoxHeight: 18,
        inputDateFormat: '%d %b %Y',
        inputEditDateFormat: '%Y-%m-%d',
        buttons: [
          {
            type: 'day',
            count: 1,
            text: '1D',
            title: 'View 1 day'
          },
          {
            type: 'month',
            count: 1,
            text: '1M',
            title: 'View 1 month'
          },
          {
            type: 'month',
            count: 3,
            text: '3M',
            title: 'View 3 months'
          },
          {
            type: 'month',
            count: 6,
            text: '6M',
            title: 'View 6 months'
          },
          {
            type: 'year',
            count: 1,
            text: '1Y',
            title: 'View 1 year'
          },
          {
            type: 'all',
            text: 'All',
            title: 'View all data'
          }
        ],
        buttonTheme: {
          fill: '#FFFFFF',
          stroke: '#D1D5DB',
          'stroke-width': 1,
          r: 6,
          style: {
            fontSize: '11px',
            fontWeight: '500',
            color: '#6B7280'
          },
          states: {
            select: {
              fill: '#6366F1',
              stroke: '#6366F1',
              style: { color: '#ffffff', fontWeight: '600' }
            },
            hover: {
              fill: '#F3F4F6',
              stroke: '#9CA3AF',
              style: { color: '#1F2937' }
            }
          }
        },
        buttonSpacing: 8 // Space between buttons
      },
      tooltip: {
        enabled: !compact, // Desktop only; compact uses formatter guard + CSS
        split: false,
        shared: true,
        useHTML: true,
        outside: true,
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        borderColor: '#E2E8F0',
        borderRadius: 8,
        borderWidth: 1,
        shadow: true,
        style: {
          zIndex: 9999,
          pointerEvents: 'none'
        },
        formatter: function() {
          if (compact || !tooltipEnabledRef.current) {
            pushLeftPanelTooltipData(null);
            return false;
          }
          if (!this.points || this.points.length === 0) {
            pushLeftPanelTooltipData(null);
            return false;
          }
          const date = Highcharts.dateFormat('%B %d, %Y %H:%M', this.x);

          // Collect all series values into a map by series name
          const vals = {};
          this.points.forEach(point => {
            vals[point.series.name] = point.y;
          });

          // generationTotal = all stacked positive series (excludes Demand and Storage Charge)
          const excludeFromTotal = new Set(['Demand', 'Battery Charge', 'Pumped Charge']);
          let generationTotal = 0;
          this.points.forEach(point => {
            if (!excludeFromTotal.has(point.series.name)) {
              generationTotal += (point.y || 0);
            }
          });

          // Helper: render a single tooltip row
          const row = (label, value, color) =>
            `<div style="margin-bottom:4px;display:flex;align-items:center;">
              <span style="display:inline-block;width:10px;height:10px;background-color:${color};border-radius:2px;margin-right:6px;flex-shrink:0;"></span>
              <span style="font-weight:500;color:#475569;">${label}:</span>
              <span style="font-weight:600;color:${color};margin-left:6px;">${Highcharts.numberFormat(value, 2)} GW</span>
            </div>`;

          let content = `<div style="padding:8px;">
            <div style="font-weight:600;color:#1E293B;margin-bottom:8px;">${date}</div>`;

          // 1. Demand always first (fixed indigo color)
          if (vals['Demand'] != null) {
            content += row('Demand', vals['Demand'], '#6366F1');
          }

          // 2. All generation series in stack order (bottom → top)
          const generationRows = [];
          hourlyTooltipGenerationRowDefs.forEach(({ label, colorKey, fallback, color: fixedColor }) => {
            const c = fixedColor || getFuelColor(colorKey);
            const color = (c && c !== '#7F8C8D') ? c : (fallback || '#7F8C8D');
            generationRows.push({
              label,
              value: vals[label] ?? null,
              color
            });
            if (vals[label] != null && vals[label] !== 0) {
              content += row(label, vals[label], color);
            }
          });

          // 3. Battery / pumped charge shown separately (negative value → display absolute)
          if (vals['Battery Charge'] != null && vals['Battery Charge'] !== 0) {
            content += row('Battery Charge', Math.abs(vals['Battery Charge']), '#D8BFD8');
          }
          if (vals['Pumped Charge'] != null && vals['Pumped Charge'] !== 0) {
            content += row('Pumped Charge', Math.abs(vals['Pumped Charge']), '#5DADE2');
          }

          // 4. Gap / Surplus at bottom
          if (vals['Demand'] != null && generationTotal > 0) {
            const gap = vals['Demand'] - generationTotal;
            const gapColor = gap > 0 ? '#EF4444' : '#10B981';
            const gapLabel = gap > 0 ? 'Gap (Additional needed)' : 'Surplus';
            content += `
              <div style="margin-top:8px;padding-top:8px;border-top:1px solid #E2E8F0;">
                <span style="font-weight:600;color:${gapColor};">${gapLabel}:</span>
                <span style="font-weight:600;color:${gapColor};margin-left:6px;">${Highcharts.numberFormat(Math.abs(gap), 2)} GW</span>
              </div>`;
          }

          if (leftPanelExpandedRef.current) {
            pushLeftPanelTooltipData({
              x: this.x,
              date,
              demand: vals['Demand'] ?? null,
              generationRows,
              storageCharge: vals['Battery Charge'] != null && vals['Battery Charge'] !== 0 ? Math.abs(vals['Battery Charge']) : null,
              pumpedStorageCharge: vals['Pumped Charge'] != null && vals['Pumped Charge'] !== 0 ? Math.abs(vals['Pumped Charge']) : null,
              gap: vals['Demand'] != null && generationTotal > 0 ? (vals['Demand'] - generationTotal) : null
            });
            return false;
          }

          pushLeftPanelTooltipData(null);

          content += '</div>';
          return content;
        }
      },
      exporting: {
        enabled: true,
        buttons: {
          contextButton: {
            enabled: !isMobileViewport,
            align: 'right',
            verticalAlign: 'top',
            x: -5,   // Small offset from right edge
            y: compact ? 32 : 8,
            symbolStrokeWidth: 2,
            symbolFill: '#666666',
            symbolStroke: '#666666',
            theme: {
              fill: 'transparent',
              stroke: 'none',
              r: 6,
              states: {
                hover: { fill: '#F3F4F6' },
                select: { fill: '#E5E7EB' }
              }
            }
          }
        },
        menuItemDefinitions: {
          viewFullscreen: { text: 'View in full screen' },
          printChart: { text: 'Print chart' },
          separator: { separator: true },
          downloadPNG: { text: 'Download PNG' },
          downloadJPEG: { text: 'Download JPEG' },
          downloadPDF: { text: 'Download PDF' },
          downloadSVG: { text: 'Download SVG' }
        },
        menuItems: [
          'viewFullscreen',
          'printChart',
          'separator',
          'downloadPNG',
          'downloadJPEG',
          'downloadPDF',
          'downloadSVG'
        ]
      },
      plotOptions: {
        series: {
          events: {
            // Highcharts 11 fires legendItemClick AFTER the default visibility
            // toggle runs (see Legend.js: itemClick -> defaultItemClick -> setVisible
            // is invoked first, then 'legendItemClick' is fired on the series).
            // Capturing visibility there required reading `!this.visible` which
            // ended up inverted across Generate refreshes. Use the canonical
            // `hide`/`show` series events instead — they fire after setVisible
            // has committed `series.visible`, so the value is always correct
            // for legend clicks AND for the right-click "isolate" path which
            // calls setVisible directly.
            hide: function() {
              if (isRestoringHourlyChartStateRef.current) return;
              const key = this?.options?.id || this?.name;
              if (key) {
                hourlyChartStateRef.current.legendVisibility[key] = false;
              }
            },
            show: function() {
              if (isRestoringHourlyChartStateRef.current) return;
              const key = this?.options?.id || this?.name;
              if (key) {
                hourlyChartStateRef.current.legendVisibility[key] = true;
              }
            }
          },
          states: {
            inactive: {
              enabled: compact ? true : tooltipEnabledRef.current
            }
          }
        },
        column: {
          borderWidth: 0,
          grouping: false,
          pointPadding: 0,
          groupPadding: 0,
          dataGrouping: {
            enabled: false
          }
        },
        line: {
          marker: { enabled: false },
          dataGrouping: {
            enabled: false
          }
        }
      },
      // HTML legend (HourlyChartDesktopLegend) — labels stay text-width; HC legend uses tabular columns
      legend: { enabled: false },
      credits: { enabled: false },
      responsive: HIGHCHARTS_NARROW_LAYOUT_RESPONSIVE
    };
  }, [
    hasHourlyChartData,
    captureHourlyLegendVisibility,
    hourlyFullscreenPlotHeight,
    hourlyMobileFullscreenOpen,
    isCompactViewport,
    isMobileViewport,
    hourlyTooltipGenerationRowDefs,
    pushLeftPanelTooltipData,
  ]);

  const chartOptions = useMemo(() => {
    if (!hourlyChartOptionsShell || !hourlyChartSeries) return null;
    return { ...hourlyChartOptionsShell, series: hourlyChartSeries };
  }, [hourlyChartOptionsShell, hourlyChartSeries]);


  const voFlexFuelToggles = useMemo(() => {
    if (simulation?.dispatchMode !== 'vo-report-tech') return null;
    const rows = simulation?.flexDispatchOrder;
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const byFuel = { bio: [], coal: [], gas: [], oil: [] };
    for (const row of rows) {
      if (row?.fuel && byFuel[row.fuel]) byFuel[row.fuel].push(row);
    }
    const toggles = VO_FLEX_CHART_FUELS.filter((def) =>
      hasExpandableVoSubfuels(byFuel[def.fuel])
    ).map((def) => ({
      fuel: def.fuel,
      label: def.parentName.replace(' (dispatch)', ''),
      collapsed: voFlexFuelCollapsed[def.fuel] !== false,
    }));
    return toggles.length ? toggles : null;
  }, [simulation?.dispatchMode, simulation?.flexDispatchOrder, voFlexFuelCollapsed]);

  const handleToggleVoFlexFuel = useCallback((fuel) => {
    if (voFlexFuelToggleBusy || isFlexTogglePending) return false;
    setVoFlexFuelToggleActiveFuel(fuel);
    setVoFlexFuelToggleBusy(true);
    voFlexFuelToggleGenRef.current += 1;
    voFlexFuelTogglePendingUnlockRef.current = true;
    startFlexToggleTransition(() => {
      setVoFlexFuelCollapsed((prev) => ({
        ...prev,
        // undefined/true → expand (false); false → collapse (true)
        [fuel]: prev[fuel] === false ? true : false,
      }));
    });
    return true;
  }, [voFlexFuelToggleBusy, isFlexTogglePending, startFlexToggleTransition]);

  /**
   * Unlock after collapsed state has produced new chartOptions and the browser
   * has painted (Highcharts update runs in the same turn / following effects).
   * Do not unlock merely because busy flipped true — that races the rebuild.
   */
  useEffect(() => {
    if (!voFlexFuelTogglePendingUnlockRef.current) return undefined;
    const gen = voFlexFuelToggleGenRef.current;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (
          gen === voFlexFuelToggleGenRef.current &&
          voFlexFuelTogglePendingUnlockRef.current
        ) {
          voFlexFuelTogglePendingUnlockRef.current = false;
          setVoFlexFuelToggleBusy(false);
          setVoFlexFuelToggleActiveFuel(null);
        }
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [chartOptions, voFlexFuelCollapsed]);

  const flexFuelToggleBusy = voFlexFuelToggleBusy || isFlexTogglePending;

  const hourlyDataReady = useMemo(() => {
    return Boolean(
      chartData?.hourlyProfile &&
      simulation &&
      !loading &&
      !capacityLoading &&
      !solarLoading &&
      !windLoading &&
      !windOffLoading &&
      chartOptions
    );
  }, [
    capacityLoading,
    chartData,
    chartOptions,
    loading,
    simulation,
    solarLoading,
    windLoading,
    windOffLoading
  ]);

  const hourlyChartFullyReady = hourlyDataReady && hourlyChartLayoutReady;
  hourlyDataReadyRef.current = hourlyDataReady;

  const handleHourlyDesktopLegendHeight = useCallback((height) => {
    setHourlyDesktopLegendHeight((prev) => (Math.abs(prev - height) < 1 ? prev : height));
  }, []);

  useEffect(() => {
    const seriesCount = chartOptions?.series?.length ?? 0;
    if (seriesCount > 0) {
      setHourlyDesktopLegendHeight(getHourlyDesktopLegendSlotHeight(seriesCount));
    }
  }, [chartOptions?.series?.length]);

  useEffect(() => {
    resetHourlyChartLayoutReady('country_or_year');
  }, [countryIso, formData.year, resetHourlyChartLayoutReady]);

  useEffect(() => {
    setHourlyVisibleMonthIndex(0);
    pendingHourlyChartFocusMsRef.current = null;
    hourlyChartStateRef.current.zoom = null;
  }, [countryIso, formData.year]);

  useEffect(() => {
    if (loading) resetHourlyChartLayoutReady('demand_loading');
  }, [loading, resetHourlyChartLayoutReady]);

  const hourlySeriesCount = chartOptions?.series?.length ?? 0;

  useEffect(() => {
    if (!hourlyDataReady || hourlySeriesCount === 0) return undefined;
    const chart = chartRef.current?.chart;
    if (!chart || chart.destroyed) return undefined;
    restoreHourlyChartInteractionState(chart);
    scheduleRevealHourlyChartLayout(chart, 'hourlyDataReady');
  }, [
    hourlySeriesCount,
    hourlyDataReady,
    scheduleRevealHourlyChartLayout,
    restoreHourlyChartInteractionState
  ]);

  // Reflow hourly chart when switching compact/desktop layout (legend + margins change).
  const prevCompactForHourlyLayoutRef = useRef(isCompactViewport);
  useEffect(() => {
    const compactChanged = prevCompactForHourlyLayoutRef.current !== isCompactViewport;
    prevCompactForHourlyLayoutRef.current = isCompactViewport;
    if (!compactChanged) return undefined;
    const chart = chartRef.current?.chart;
    if (!chart || !hourlyDataReady) return undefined;
    hourlyLayoutRevealedSignatureRef.current = null;
    scheduleRevealHourlyChartLayout(chart, 'compact_viewport_change');
  }, [hourlyDataReady, isCompactViewport, scheduleRevealHourlyChartLayout]);

  useEffect(() => {
    if (!isMobileViewport && hourlyMobileFullscreenOpen) {
      setHourlyMobileFullscreenOpen(false);
    }
  }, [hourlyMobileFullscreenOpen, isMobileViewport]);

  useEffect(() => {
    if (!hourlyMobileFullscreenOpen || !isMobileViewport) return undefined;
    const bodyHeight = hourlyFullscreenBodyHeightRef.current;
    if (bodyHeight > 0) {
      setHourlyFullscreenPlotHeight((prev) => {
        const next = computeFullscreenStockChartHeight(bodyHeight, hourlyFullscreenLegendHeight);
        return Math.abs(prev - next) < 2 ? prev : next;
      });
    }
    const host = hourlyFullscreenChartHostRef.current;
    if (!host || typeof ResizeObserver === 'undefined') {
      requestAnimationFrame(reportHourlyFullscreenChartHostHeight);
      return undefined;
    }
    const observer = new ResizeObserver(() => reportHourlyFullscreenChartHostHeight());
    observer.observe(host);
    reportHourlyFullscreenChartHostHeight();
    return () => observer.disconnect();
  }, [
    hourlyFullscreenLegendHeight,
    hourlyMobileFullscreenOpen,
    isMobileViewport,
    reportHourlyFullscreenChartHostHeight
  ]);

  const prevMobileFullscreenOpenRef = useRef(false);
  useEffect(() => {
    const isOpen = hourlyMobileFullscreenOpen && isMobileViewport;
    const justOpened = isOpen && !prevMobileFullscreenOpenRef.current;
    prevMobileFullscreenOpenRef.current = isOpen;
    if (!justOpened || !hourlyDataReady) return undefined;
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      const chart = chartRef.current?.chart;
      if (!chart || chart.destroyed) return;
      restoreHourlyChartInteractionState(chart);
      scheduleRevealHourlyChartLayout(chart, 'mobile_fullscreen');
    };
    const frameId = requestAnimationFrame(run);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
    };
  }, [
    hourlyDataReady,
    hourlyMobileFullscreenOpen,
    isMobileViewport,
    restoreHourlyChartInteractionState,
    scheduleRevealHourlyChartLayout
  ]);

  // No tooltips on compact viewports — clear any mirrored left-panel tooltip.
  useEffect(() => {
    if (isCompactViewport) {
      pushLeftPanelTooltipData(null);
    }
  }, [isCompactViewport, pushLeftPanelTooltipData]);

  // Resolve profile chart color at render time so async fuel-color cache
  // initialization cannot permanently lock this card to fallback gray.
  // Toggle skeleton via DOM (no React state) so panel click doesn't trigger heavy re-render.
  // On mobile (flex-col), collapsing the left panel often does NOT change the chart container
  // width — only vertical layout — so ResizeObserver never fires and `is-resizing` stuck.
  const flushHourlyChartReflowAndResizeComplete = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const chart = chartRef.current?.chart;
        if (chart && typeof chart.reflow === 'function') {
          chart.reflow();
          chart.redraw?.(false);
        }
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            onResizeCompleteRef.current?.();
            onResizeCompleteRef.current = null;
          });
        });
      });
    });
  }, []);

  const startChartResize = useCallback(() => {
    const wrapper = chartResizeWrapperRef.current;
    if (!wrapper) return;
    if (panelResizeSafetyRef.current) {
      clearTimeout(panelResizeSafetyRef.current);
      panelResizeSafetyRef.current = null;
    }
    wrapper.classList.add('is-resizing');
    onResizeCompleteRef.current = () => {
      if (panelResizeSafetyRef.current) {
        clearTimeout(panelResizeSafetyRef.current);
        panelResizeSafetyRef.current = null;
      }
      wrapper.classList.remove('is-resizing');
      onResizeCompleteRef.current = null;
    };
    // Mobile flex-col toggles may not resize the chart width; ensure skeleton always clears.
    panelResizeSafetyRef.current = setTimeout(() => {
      panelResizeSafetyRef.current = null;
      wrapper.classList.remove('is-resizing');
      onResizeCompleteRef.current = null;
    }, 2500);
    flushHourlyChartReflowAndResizeComplete();
  }, [flushHourlyChartReflowAndResizeComplete]);
  startChartResizeRef.current = startChartResize;

  /** Light chart reflow when full resize overlay is not needed (e.g. after Generate on mobile). */
  const scheduleDeferredChartReflow = useCallback(() => {
    requestAnimationFrame(() => {
      const chart = chartRef.current?.chart;
      if (chart && typeof chart.reflow === 'function') chart.reflow();
    });
  }, []);
  scheduleDeferredChartReflowRef.current = scheduleDeferredChartReflow;

  const hourlyVisibleMonthIndexRef = useRef(hourlyVisibleMonthIndex);
  hourlyVisibleMonthIndexRef.current = hourlyVisibleMonthIndex;

  const applyHourlyChartZoomAroundTimestamp = useCallback(
    (targetMs) => {
      const chart = chartRef.current?.chart;
      const xAxis = chart?.xAxis?.[0];
      if (!chart || !xAxis || typeof targetMs !== 'number') return;

      const year = formData?.year || chartData?.year || 2022;
      let datasetStart;
      let datasetEnd;
      if (isCompactViewportRef.current) {
        const monthIndex = hourlyVisibleMonthIndexRef.current;
        const bounds = getMonthHourBounds(year, monthIndex);
        datasetStart = bounds.startMs;
        datasetEnd = bounds.endMsExclusive;
      } else {
        datasetStart = Date.UTC(year, 0, 1, 0, 0, 0);
        datasetEnd = datasetStart + 8760 * 3600000;
      }

      const weekMs = 7 * 24 * 3600000;
      const halfWeekMs = weekMs / 2;

      let min = targetMs - halfWeekMs;
      let max = targetMs + halfWeekMs;

      if (min < datasetStart) {
        min = datasetStart;
        max = Math.min(datasetEnd, datasetStart + weekMs);
      }
      if (max > datasetEnd) {
        max = datasetEnd;
        min = Math.max(datasetStart, datasetEnd - weekMs);
      }

      startChartResize();
      requestAnimationFrame(() => {
        xAxis.setExtremes(min, max, true, false);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            onResizeCompleteRef.current?.();
            onResizeCompleteRef.current = null;
          });
        });
      });
    },
    [chartData?.year, formData?.year, startChartResize]
  );

  const focusHourlyChartAroundTimestamp = useCallback(
    (targetMs) => {
      if (typeof targetMs !== 'number') return;
      const year = formData?.year || chartData?.year || 2022;
      if (isCompactViewportRef.current) {
        const monthIndex = getUtcMonthIndexForYear(year, targetMs);
        if (monthIndex !== hourlyVisibleMonthIndexRef.current) {
          skipNextCompactMonthFitRef.current = true;
          pendingHourlyChartFocusMsRef.current = targetMs;
          clearHourlyChartZoomState();
          setHourlyVisibleMonthIndex(monthIndex);
          return;
        }
      }
      applyHourlyChartZoomAroundTimestamp(targetMs);
      skipNextCompactMonthFitRef.current = false;
    },
    [applyHourlyChartZoomAroundTimestamp, chartData?.year, clearHourlyChartZoomState, formData?.year]
  );

  useEffect(() => {
    const pendingMs = pendingHourlyChartFocusMsRef.current;
    if (pendingMs == null || !hourlyDataReady) return undefined;
    pendingHourlyChartFocusMsRef.current = null;
    let scrollTimeoutId = 0;
    const frameId = requestAnimationFrame(() => {
      scrollToHourlyChartSection();
      applyHourlyChartZoomAroundTimestamp(pendingMs);
      skipNextCompactMonthFitRef.current = false;
      if (isCompactViewportRef.current) {
        scrollTimeoutId = window.setTimeout(scrollToHourlyChartSection, 150);
      }
    });
    return () => {
      cancelAnimationFrame(frameId);
      if (scrollTimeoutId) clearTimeout(scrollTimeoutId);
    };
  }, [
    applyHourlyChartZoomAroundTimestamp,
    hourlyDataReady,
    hourlyVisibleMonthIndex,
    scrollToHourlyChartSection,
  ]);

  // After prev/next month: fit x-axis to full month once new series data is in the chart.
  useEffect(() => {
    if (!isCompactViewport || !hourlyDataReady) return undefined;
    if (skipNextCompactMonthFitRef.current) return undefined;

    scheduleCompactMonthViewportFit(hourlyVisibleMonthIndex);
    return undefined;
  }, [
    hourlyVisibleMonthIndex,
    hourlyDataReady,
    isCompactViewport,
    scheduleCompactMonthViewportFit,
  ]);

  /** Desktop sidebar/tab: skeleton overlay while chart reflows after width change. */
  const handleDesktopPanelsLayoutChange = useCallback(() => {
    startChartResizeRef.current?.();
  }, []);

  /** Mobile/tablet Inputs ↔ System Balance: light reflow only (no skeleton — faster tab switch). */
  const handleMobilePanelsLayoutChange = useCallback(() => {
    scheduleDeferredChartReflowRef.current?.();
  }, []);

  const handleRightPanelCollapse = useCallback(() => {
    if (isCompactViewportRef.current) {
      mobilePanelsRef.current?.closePanels();
      return;
    }
    desktopRightPanelRef.current?.collapse();
  }, []);

  const handleMobileInputsPanelCollapse = useCallback(() => {
    mobilePanelsRef.current?.closePanels();
  }, []);

  const focusMaxShortageAtTimestamp = useCallback(
    (ms) => {
      if (typeof ms !== 'number') return;
      skipNextCompactMonthFitRef.current = true;
      scrollToHourlyChartSection();
      focusHourlyChartAroundTimestamp(ms);
      if (scrollAfterFocusTimeoutRef.current != null) {
        clearTimeout(scrollAfterFocusTimeoutRef.current);
      }
      if (isCompactViewportRef.current) {
        scrollAfterFocusTimeoutRef.current = window.setTimeout(() => {
          scrollAfterFocusTimeoutRef.current = null;
          scrollToHourlyChartSection();
        }, 150);
      }
    },
    [focusHourlyChartAroundTimestamp, scrollToHourlyChartSection]
  );

  const displayedMaxShortageEvent =
    topMaxShortageEvents[clampedMaxShortageNavIndex] ??
    (balanceMetrics?.maxShortageTimestampMs != null
      ? {
          timestampMs: balanceMetrics.maxShortageTimestampMs,
          timestamp: balanceMetrics.maxShortageTimestamp,
        }
      : null);

  const handleRightPanelMaxShortageClick = useCallback(() => {
    const ms = displayedMaxShortageEvent?.timestampMs;
    if (typeof ms !== 'number') return;
    setMaxShortageChartNavVisible(true);
    focusMaxShortageAtTimestamp(ms);
  }, [displayedMaxShortageEvent?.timestampMs, focusMaxShortageAtTimestamp]);

  const focusMaxShortageAtTimestampRef = useLatestRef(focusMaxShortageAtTimestamp);
  const topMaxShortageEventsRef = useLatestRef(topMaxShortageEvents);
  const debouncedMaxShortageNavFocus = useStableDebouncedCallback((index) => {
    const event = topMaxShortageEventsRef.current[index];
    if (!event || typeof event.timestampMs !== 'number') return;
    focusMaxShortageAtTimestampRef.current(event.timestampMs);
  }, 250);

  const handleMaxShortageNavPrev = useCallback(() => {
    if (clampedMaxShortageNavIndex <= 0 || topMaxShortageEvents.length <= 1) return;
    const nextIndex = clampedMaxShortageNavIndex - 1;
    setMaxShortageNavIndex(nextIndex);
    debouncedMaxShortageNavFocus(nextIndex);
  }, [clampedMaxShortageNavIndex, debouncedMaxShortageNavFocus, topMaxShortageEvents.length]);

  const handleMaxShortageNavNext = useCallback(() => {
    if (
      clampedMaxShortageNavIndex >= topMaxShortageEvents.length - 1 ||
      topMaxShortageEvents.length <= 1
    ) {
      return;
    }
    const nextIndex = clampedMaxShortageNavIndex + 1;
    setMaxShortageNavIndex(nextIndex);
    debouncedMaxShortageNavFocus(nextIndex);
  }, [clampedMaxShortageNavIndex, debouncedMaxShortageNavFocus, topMaxShortageEvents.length]);

  // Reflow chart when container resizes (e.g. left/right panel collapse/expand).
  // Debounce so we only reflow after resize settles; then wait for paint before hiding skeleton.
  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el) return;
    const debouncedReflow = debounce(() => {
      flushHourlyChartReflowAndResizeComplete();
    }, 200);
    const ro = new ResizeObserver(debouncedReflow);
    ro.observe(el);
    return () => {
      if (debouncedReflow.cancel) debouncedReflow.cancel();
      ro.disconnect();
    };
  }, [flushHourlyChartReflowAndResizeComplete]);

  // Cancel pending timers/debouncers on unmount to avoid setState after teardown.
  useEffect(() => () => {
    if (panelResizeSafetyRef.current != null) {
      clearTimeout(panelResizeSafetyRef.current);
      panelResizeSafetyRef.current = null;
    }
    if (hourlyRevealTimeoutRef.current != null) {
      clearTimeout(hourlyRevealTimeoutRef.current);
      hourlyRevealTimeoutRef.current = null;
    }
    if (scrollAfterFocusTimeoutRef.current != null) {
      clearTimeout(scrollAfterFocusTimeoutRef.current);
      scrollAfterFocusTimeoutRef.current = null;
    }
  }, []);


  const showHourlyChartSkeleton = useMemo(() => {
    if (error || capacityError) return false;
    return !hourlyChartFullyReady;
  }, [capacityError, error, hourlyChartFullyReady]);

  // Hourly Generation and Demand chart body (inline card or mobile full-screen modal)
  const renderHourlyGenerationAndDemandChartCard = useCallback((surface = 'inline') => {
    const isFullscreenSurface = surface === 'fullscreen';
    const isCompactOrFullscreen = isCompactViewport || isFullscreenSurface;
    const showDesktopTopLegend = !isCompactViewport && !isFullscreenSurface;
    const showCompactBottomLegend = isCompactOrFullscreen;

    const shouldRenderChart = hourlyDataReady && Boolean(chartOptions);
    const chartContentHidden = shouldRenderChart && !hourlyChartLayoutReady;
    const chartHiddenClassSuffix = chartContentHidden ? ' invisible' : '';
    const chartHiddenLegendClassName = chartContentHidden ? 'invisible' : undefined;

    const blockingMessage = error || capacityError;
    const showLoadingOverlay = Boolean(blockingMessage || chartContentHidden);

    const desktopTotalHeight = hourlyDesktopLegendHeight + HOURLY_DESKTOP_PLOT_HEIGHT;
    const compactChartHeightPx = isFullscreenSurface
      ? hourlyFullscreenPlotHeight
      : getHourlyCompactChartHeight(isMobileViewport);

    const buildSurfaceLayout = () => {
      if (isFullscreenSurface) {
        return {
          outerClass: 'relative flex h-full min-h-0 w-full flex-col',
          outerStyle: { height: '100%', minHeight: 0 },
          resizeWrapperClass: 'relative flex min-h-0 w-full flex-1 flex-col group',
          chartHostClass:
            'relative z-10 flex min-h-0 w-full flex-1 transition-none group-[.is-resizing]:invisible',
          chartHostShrinkClass: '',
          chartHostStyle: { minHeight: 0 },
          chartHostRef: hourlyFullscreenChartHostRef,
          chartContainerStyle: { height: '100%', width: '100%', overflow: 'visible' },
          skeletonWrapperClass: 'relative min-h-[280px] w-full',
          messageWrapperClass: 'min-h-[240px] py-6',
          compactLegendOnHeightChange: handleHourlyFullscreenLegendHeight,
        };
      }

      if (isCompactViewport) {
        return {
          outerClass: 'w-full min-h-[200px] h-auto relative',
          outerStyle: {
            minHeight: getHourlyCompactCardMinHeight(isMobileViewport),
            height: 'auto',
          },
          resizeWrapperClass: 'relative flex w-full h-full min-h-0 flex-col group',
          chartHostClass:
            'relative z-10 w-full h-full min-h-0 transition-none group-[.is-resizing]:invisible',
          chartHostShrinkClass: ' shrink-0',
          chartHostStyle: {
            height: compactChartHeightPx,
            minHeight: getHourlyCompactChartHostMinHeight(isMobileViewport),
          },
          chartHostRef: undefined,
          chartContainerStyle: { height: '100%', width: '100%', overflow: 'visible' },
          skeletonWrapperClass: 'relative min-h-[280px] w-full',
          messageWrapperClass: 'min-h-[240px] py-6',
          compactLegendOnHeightChange: undefined,
        };
      }

      return {
        outerClass: 'relative flex flex-col',
        outerStyle: { height: desktopTotalHeight },
        resizeWrapperClass: 'relative flex flex-col w-full h-full group',
        chartHostClass:
          'relative z-10 w-full shrink-0 transition-none group-[.is-resizing]:invisible',
        chartHostShrinkClass: ' shrink-0',
        chartHostStyle: { height: HOURLY_DESKTOP_PLOT_HEIGHT },
        chartHostRef: undefined,
        chartContainerStyle: {
          height: HOURLY_DESKTOP_PLOT_HEIGHT,
          width: '100%',
          overflow: 'visible',
        },
        skeletonWrapperClass: 'absolute inset-0',
        messageWrapperClass: 'h-full',
        compactLegendOnHeightChange: undefined,
      };
    };

    const layout = buildSurfaceLayout();

    const renderDesktopTopLegend = () => {
      if (!showDesktopTopLegend || !chartOptions?.series) return null;
      return (
        <HourlyChartDesktopLegend
          series={chartOptions.series}
          chartRef={chartRef}
          onHeightChange={handleHourlyDesktopLegendHeight}
          className={chartHiddenLegendClassName}
          flexFuelToggles={voFlexFuelToggles}
          onToggleFlexFuel={handleToggleVoFlexFuel}
          flexFuelToggleBusy={flexFuelToggleBusy}
          flexFuelToggleActiveFuel={voFlexFuelToggleActiveFuel}
        />
      );
    };

    const renderCompactBottomLegend = () => {
      if (!showCompactBottomLegend || !chartOptions?.series) return null;
      return (
        <HourlyChartDesktopLegend
          series={chartOptions.series}
          chartRef={chartRef}
          onHeightChange={layout.compactLegendOnHeightChange}
          className={`shrink-0 border-t border-gray-100 pt-1.5 pb-0${chartHiddenClassSuffix}`}
          flexFuelToggles={voFlexFuelToggles}
          onToggleFlexFuel={handleToggleVoFlexFuel}
          flexFuelToggleBusy={flexFuelToggleBusy}
          flexFuelToggleActiveFuel={voFlexFuelToggleActiveFuel}
        />
      );
    };

    const renderChartHost = () => (
      <div
        ref={layout.chartHostRef}
        className={`${layout.chartHostClass}${layout.chartHostShrinkClass} -mt-0.5${chartHiddenClassSuffix}`}
        style={layout.chartHostStyle}
      >
        <ResponsiveHighchartsReact
          highcharts={Highcharts}
          options={chartOptions}
          constructorType="stockChart"
          updateArgs={[true, true, false]}
          containerProps={{ style: layout.chartContainerStyle }}
          ref={(r) => {
            chartRef.current = r;
          }}
        />
        <div
          ref={chartOverlayRef}
          className="absolute inset-0 z-30 flex items-center justify-center bg-white/80 backdrop-blur-[1px] opacity-0 pointer-events-none"
          role="status"
          aria-live="polite"
          aria-label="Chart is updating"
        >
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm font-medium text-gray-600">Loading...</span>
          </div>
        </div>
      </div>
    );

    const renderLoadingOverlay = () => {
      if (!showLoadingOverlay) return null;
      return (
        <div className="absolute inset-0 z-40">
          {blockingMessage ? (
            <div className="absolute inset-0 bg-white/90 backdrop-blur-[1px] flex items-center justify-center text-amber-600 text-sm px-4 text-center">
              {blockingMessage}
            </div>
          ) : (
            <HourlyGenerationAndDemandChartSkeleton visible />
          )}
        </div>
      );
    };

    const renderMessagePlaceholder = (message) => (
      <div
        className={`flex items-center justify-center text-gray-400 ${layout.messageWrapperClass}`}
      >
        {message}
      </div>
    );

    const renderFallbackContent = () => {
      if (blockingMessage) {
        return (
          <div className="h-full flex items-center justify-center text-amber-600 text-sm">
            {blockingMessage}
          </div>
        );
      }
      if (showHourlyChartSkeleton) {
        return (
          <div className={layout.skeletonWrapperClass}>
            <HourlyGenerationAndDemandChartSkeleton visible />
          </div>
        );
      }
      if (chartData) {
        return renderMessagePlaceholder('Chart is preparing...');
      }
      return renderMessagePlaceholder('Chart will appear here');
    };

    const renderChartBody = () => (
      <div ref={chartResizeWrapperRef} className={layout.resizeWrapperClass}>
        {renderDesktopTopLegend()}
        {renderChartHost()}
        {renderCompactBottomLegend()}
        <HourlyGenerationAndDemandChartSkeleton />
        {renderLoadingOverlay()}
      </div>
    );

    const showMonthNavigator = isCompactViewport && hourlyDataReady;
    const showSplitMaxShortageNav =
      isMobileViewport &&
      maxShortageChartNavVisible &&
      topMaxShortageEvents.length > 0 &&
      Boolean(displayedMaxShortageEvent?.timestamp);

    return (
      <div
        ref={chartContainerRef}
        className={`hourly-generation-demand-chart ${layout.outerClass} bg-white overflow-visible`}
        style={layout.outerStyle}
      >
        {showMonthNavigator ? (
          <HourlyChartMonthNavigator
            year={hourlyChartYear}
            monthIndex={hourlyVisibleMonthIndex}
            onPrevious={handleHourlyVisibleMonthPrevious}
            onNext={handleHourlyVisibleMonthNext}
            showMaxShortageNav={showSplitMaxShortageNav}
            maxShortageTimestamp={displayedMaxShortageEvent?.timestamp}
            maxShortageNavIndex={clampedMaxShortageNavIndex}
            maxShortageEventCount={topMaxShortageEvents.length}
            onMaxShortagePrevious={handleMaxShortageNavPrev}
            onMaxShortageNext={handleMaxShortageNavNext}
          />
        ) : null}
        {shouldRenderChart ? renderChartBody() : renderFallbackContent()}
      </div>
    );
  }, [
    capacityError,
    chartData,
    chartOptions,
    error,
    flexFuelToggleBusy,
    voFlexFuelToggleActiveFuel,
    handleHourlyDesktopLegendHeight,
    handleHourlyFullscreenLegendHeight,
    handleHourlyVisibleMonthNext,
    handleHourlyVisibleMonthPrevious,
    handleMaxShortageNavPrev,
    handleMaxShortageNavNext,
    clampedMaxShortageNavIndex,
    maxShortageChartNavVisible,
    topMaxShortageEvents.length,
    displayedMaxShortageEvent?.timestamp,
    handleToggleVoFlexFuel,
    hourlyChartLayoutReady,
    hourlyChartYear,
    hourlyDataReady,
    hourlyDesktopLegendHeight,
    hourlyFullscreenPlotHeight,
    hourlyVisibleMonthIndex,
    isCompactViewport,
    isMobileViewport,
    showHourlyChartSkeleton,
    voFlexFuelToggles,
  ]);

  const simulatorUtilizationMapsGridCard = useMemo(
    () => (
      <SimulatorUtilizationMapsGrid
        solarLoading={solarLoading}
        windLoading={windLoading}
        windMapRef={simulatorWindMapRef}
        windOnshoreLayerRef={simulatorWindLayerRef}
        windOffshoreLayerRef={simulatorWindOffLayerRef}
        windOnshoreLayerVisibleRef={windOnshoreLayerVisibleRef}
        windOffshoreLayerVisibleRef={windOffshoreLayerVisibleRef}
        windMapLayerGeneration={windMapLayerGeneration}
      />
    ),
    [solarLoading, windLoading, windMapLayerGeneration]
  );

  const handleDesktopInputsPanelCollapse = useCallback(() => {
    desktopInputsSidebarRef.current?.collapse();
  }, []);

  const inputsPanelCard = (
    <GenerationProfileInputsPanel
      ref={inputsPanelRef}
      inputsLiveRef={inputsLiveRef}
      demandTwhRef={demandTwhRef}
      leftPanelInputCardRef={leftPanelInputCardRef}
      demandInputAnchorRef={demandInputAnchorRef}
      lastAppliedDemandTwhInput={lastAppliedDemandTwhInput}
      lastAppliedCapacityInputs={lastAppliedCapacityInputs}
      lastAppliedDispatchableInputs={lastAppliedDispatchableInputs}
      lastAppliedVoSubtech={lastAppliedVoSubtech}
      capacityLimits={renewableCapacityLimits}
      urlSourceInputClass={urlSourceInputClass}
      chartData={chartData}
      nuclearProfile={nuclearProfile}
      solarProfile={solarProfile}
      windProfile={windProfile}
      weatherYear={weatherYear}
      onWeatherYearChange={handleWeatherYearChange}
      weatherYearsLoading={weatherYearsLoading}
      availableWeatherYears={availableWeatherYears}
      weatherYearsError={weatherYearsError}
      supplyWeatherYear={supplyWeatherYear}
      onSupplyWeatherYearChange={handleSupplyWeatherYearChange}
      isDemandGoLoading={isDemandGoLoading}
      capacitySubmitLoading={capacitySubmitLoading}
      dispatchableStorageSubmitLoading={dispatchableStorageSubmitLoading}
      onUnifiedGenerate={handleUnifiedGenerate}
      onCopyScenarioLink={handleCopySimulatorScenarioLink}
      isCompactViewport={isCompactViewport}
      onMobileCollapse={handleMobileInputsPanelCollapse}
      onDesktopCollapse={handleDesktopInputsPanelCollapse}
      isDemandGoLoadingDisabled={isDemandGoLoading}
      generationRowDefs={hourlyTooltipGenerationRowDefs}
      voCapacityByFuel={voCapacityByFuel}
    />
  );

  const mobileBalancePanelCard = useMemo(
    () => (
      <div className="w-full min-w-0 flex flex-col">
        <RightPanelMetricsCards
          balanceMetrics={balanceMetrics}
          fuelUtilization={fuelUtilization}
          voGenByFuel={voGenByFuel}
          storageCapacityGW={storageCapacityGW}
          pumpedStorageCapacityGW={pumpedStorageCapacityGW}
          nuclearAvgGW={nuclearAvgGW}
          nuclearPeakGW={nuclearPeakGW}
          solarAvgGW={solarAvgGW}
          solarPeakGW={solarPeakGW}
          windAvgGW={windAvgGW}
          windPeakGW={windPeakGW}
          hydroAvgGW={hydroAvgGW}
          hydroPeakGW={hydroPeakGW}
          onMaxShortageClick={handleRightPanelMaxShortageClick}
          maxShortageNavIndex={clampedMaxShortageNavIndex}
          maxShortageEventCount={topMaxShortageEvents.length}
          displayedMaxShortageTimestamp={displayedMaxShortageEvent?.timestamp}
          displayedMaxShortageTimestampMs={displayedMaxShortageEvent?.timestampMs}
          onMaxShortageNavPrev={handleMaxShortageNavPrev}
          onMaxShortageNavNext={handleMaxShortageNavNext}
          systemBalanceHeaderStart={
            <button
              type="button"
              onClick={handleRightPanelCollapse}
              className={MOBILE_PANEL_COLLAPSE_BTN_CLASS}
              title="Collapse panel"
              aria-label="Collapse panel"
            >
              <CollapsiblePanelIcon size={18} />
            </button>
          }
        />
      </div>
    ),
    [
      balanceMetrics,
      fuelUtilization,
      voGenByFuel,
      handleRightPanelCollapse,
      handleRightPanelMaxShortageClick,
      handleMaxShortageNavPrev,
      handleMaxShortageNavNext,
      clampedMaxShortageNavIndex,
      topMaxShortageEvents.length,
      displayedMaxShortageEvent?.timestamp,
      displayedMaxShortageEvent?.timestampMs,
      hydroAvgGW,
      hydroPeakGW,
      nuclearAvgGW,
      nuclearPeakGW,
      pumpedStorageCapacityGW,
      solarAvgGW,
      solarPeakGW,
      storageCapacityGW,
      windAvgGW,
      windPeakGW
    ]
  );

  const GenerationProfileDocumentationComponent = (
    <GenerationProfileMethodologyAccordion ref={methodologyAccordionRef} />
  );

  return (
    <div className="w-full min-w-0 overflow-hidden">
      {!unmappedBannerDismissed && (
        <SimulatorUnmappedTechBanner
          unmapped={unmappedTechs}
          onDismiss={() => setUnmappedBannerDismissed(true)}
        />
      )}
      {!vedaGapsBannerDismissed && parsedVedaLookup && !parsedShareScenario && (
        <VedaEngineDataGapsBanner
          gaps={vedaEngineResult?.gaps}
          meta={vedaEngineResult?.meta}
          extraDegradedReasons={vreProfileDegradedReasons}
          onDismiss={() => setVedaGapsBannerDismissed(true)}
        />
      )}
      <div className="flex flex-col lg:flex-row gap-4 md:gap-6 px-2 md:px-4 lg:px-6 py-2 md:py-4 lg:pb-6">
        {isCompactViewport ? (
          <MobileSimulatorProfilePanels
            ref={mobilePanelsRef}
            inputsPanel={inputsPanelCard}
            balancePanel={mobileBalancePanelCard}
            onLayoutChange={handleMobilePanelsLayoutChange}
          />
        ) : (
          <DesktopInputsSidebar
            ref={desktopInputsSidebarRef}
            onExpandedChange={handleLeftPanelExpandedChange}
            onLayoutChange={handleDesktopPanelsLayoutChange}
          >
            {inputsPanelCard}
          </DesktopInputsSidebar>
        )}

        {/* Main Content Area */}
        <div className="flex-1 min-w-0 space-y-4 sm:space-y-6">
          {/* Top Row - Timeline Chart */}
          <div
            ref={hourlyChartSectionRef}
            id="section-hourly-simulation-dashboard"
            className="min-w-0 bg-white rounded-lg sm:rounded-xl shadow-card hover:shadow-card-hover transition-shadow overflow-hidden scroll-mt-6"
          >
            <div className="px-3 sm:px-4 lg:px-6 pt-3 sm:pt-4 flex items-center justify-between flex-wrap gap-2">
              <h3 className="card-header-figma">Hourly-Simulation Dashboard</h3>
              {isMobileViewport ? (
                <button
                  type="button"
                  onClick={handleHourlyChartFullscreen}
                  disabled={loading || !hourlyDataReady || hourlyMobileFullscreenOpen}
                  className="ml-2 inline-flex flex-shrink-0 items-center justify-center p-1 text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-50"
                  title="View in full screen"
                  aria-label="View chart in full screen"
                >
                  <ArrowUpRight className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                </button>
              ) : (
                <div className="ml-auto flex shrink-0 items-center gap-2">
                  {!isCompactViewport ? (
                    <TooltipSwitch
                      id={HOURLY_DESKTOP_TOOLTIP_TOGGLE_ID}
                      chartRef={chartRef}
                      tooltipEnabledRef={tooltipEnabledRef}
                      chartOverlayRef={chartOverlayRef}
                      className="flex shrink-0 items-center"
                    />
                  ) : null}
                  <button
                    type="button"
                    onClick={handleReloadTimeline}
                    disabled={loading}
                    className="btn-outline text-sm px-2 py-1 disabled:opacity-50 flex-shrink-0"
                    title="Reload timeline"
                    aria-label="Reload timeline"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            {/* Compact: allow horizontal scroll if chart chrome overflows; desktop: avoid spurious x-scrollbars */}
            <div
              className={`w-full min-w-0 px-0 lg:px-2 ${
                isCompactViewport ? 'overflow-x-auto pb-1' : 'overflow-x-visible pb-2 lg:pb-4'
              }`}
            >
              {isMobileViewport && hourlyMobileFullscreenOpen ? (
                <div className="flex min-h-[200px] items-center justify-center px-4 py-8 text-center text-sm text-gray-500">
                  Chart is open in full screen. Close it to return here.
                </div>
              ) : (
                renderHourlyGenerationAndDemandChartCard('inline')
              )}
            </div>
          </div>

          {isMobileViewport ? (
            <ChartFullscreenModal
              open={hourlyMobileFullscreenOpen}
              onClose={handleCloseHourlyMobileFullscreen}
              title="Hourly-Simulation Dashboard"
              onBodySizeChange={handleHourlyFullscreenBodySizeChange}
            >
              {renderHourlyGenerationAndDemandChartCard('fullscreen')}
            </ChartFullscreenModal>
          ) : null}

          {simulatorUtilizationMapsGridCard}

          {/* Capacity Data Status */}
          {capacityLoading && (
            <div className="text-center text-gray-500 text-sm md:text-base">
              Loading capacity data...
            </div>
          )}
          
          {capacityError && (
            <div className="text-center text-amber-600 text-sm md:text-base bg-amber-50 px-3 py-2 rounded">
              ⚠️ Capacity data unavailable: {capacityError}
            </div>
          )}
        </div>

        <GenerationProfileRightPanel
          ref={desktopRightPanelRef}
          onLayoutChange={handleDesktopPanelsLayoutChange}
          balanceMetrics={balanceMetrics}
          fuelUtilization={fuelUtilization}
          voGenByFuel={voGenByFuel}
          storageCapacityGW={storageCapacityGW}
          pumpedStorageCapacityGW={pumpedStorageCapacityGW}
          nuclearAvgGW={nuclearAvgGW}
          nuclearPeakGW={nuclearPeakGW}
          solarAvgGW={solarAvgGW}
          solarPeakGW={solarPeakGW}
          windAvgGW={windAvgGW}
          windPeakGW={windPeakGW}
          hydroAvgGW={hydroAvgGW}
          hydroPeakGW={hydroPeakGW}
          onMaxShortageClick={handleRightPanelMaxShortageClick}
          maxShortageNavIndex={clampedMaxShortageNavIndex}
          maxShortageEventCount={topMaxShortageEvents.length}
          displayedMaxShortageTimestamp={displayedMaxShortageEvent?.timestamp}
          displayedMaxShortageTimestampMs={displayedMaxShortageEvent?.timestampMs}
          onMaxShortageNavPrev={handleMaxShortageNavPrev}
          onMaxShortageNavNext={handleMaxShortageNavNext}
        />
      </div>

      <ProcessingLogHost
        ref={processingLogRef}
        simulationReady={!!(chartData && simulation)}
        capacityError={capacityError}
        capacityLoading={capacityLoading}
        chartHasHourlyProfile={!!chartData?.hourlyProfile}
        loading={loading}
        capacityLoadingGuard={capacityLoading}
        solarLoading={solarLoading}
        windLoading={windLoading}
        windOffLoading={windOffLoading}
        solarProfileLength={solarProfile?.length ?? 0}
        windProfileLength={windProfile?.length ?? 0}
        nuclearProfileLength={nuclearProfile?.length ?? 0}
      />

      {GenerationProfileDocumentationComponent}

      {/* Error Display */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-300 text-red-700 px-4 py-2 rounded">
          {error}
        </div>
      )}

      <ApiTimingPanel />

      {showVedaDebugPanel && !vedaTracePanelDismissed ? (
        <VedaEngineTracePanel
          lookup={parsedVedaLookup}
          result={vedaEngineResult}
          trace={vedaEngineTrace}
          vreProfileTraces={vreProfileTraces}
          simulationDispatch={simulation ? {
            dispatchMode: simulation.dispatchMode,
            meritOrder: simulation.meritOrder,
            dispatchCosts: simulation.dispatchCosts,
            flexDispatchOrder: simulation.flexDispatchOrder,
          } : null}
          unmappedFlex={voUnmappedFlex}
          loading={vedaFetchLoading}
          error={vedaFetchError}
          onDismiss={() => setVedaTracePanelDismissed(true)}
        />
      ) : null}
    </div>
  );
});

GenerationProfileChart.displayName = 'GenerationProfileChart';

export default GenerationProfileChart;
